/**
 * LEGACY — being phased out in favor of `src/pages/genie/studio/AgentStudioPage.tsx`,
 * the newer hook-based rewrite of this same experience (routed at /studio/:url_name).
 * Any behavioral change made here (new query param, new condition, new effect,
 * new prop threaded into the welcome gate, etc.) must also be checked against and
 * ported to AgentStudioPage.tsx and its hooks under `src/pages/genie/studio/hooks/`
 * to avoid feature drift, until this file is retired.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Loader2, MessageSquare, Mic, MicOff, Pause, Play, Share2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import RedirectingScreen from "@/components/loading/RedirectingScreen";
import { IPhoneTapReminder } from "@/components/mobile/IPhoneTapReminder";
import { TemplateWrapper } from "@/components/page-templates";
import BrandSpinner from "@/components/ui/BrandSpinner";
import { Button } from "@/components/ui/button";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { NoiseEnvironmentSheet } from "@/components/voice/NoiseEnvironmentSheet";
import { VoiceAgentAvatar, VoiceAgentControls } from "@/components/VoiceAgent";
import { useInstallAppBanner } from "@/contexts";
import { useAgentPageByUrlNameFull } from "@/hooks/agent/useAgentPage";
import { useFilteredResources } from "@/hooks/agent/useFilteredResources";
import { useRole } from "@/hooks/auth";
import { usePublicHelpHub } from "@/hooks/help-hub/useHelpHub";
import { useAudioSessionControls } from "@/hooks/voice/useAudioSessionControls";
import { useChannelRequests } from "@/hooks/voice/useChannelRequests";
import { useConversationImageMediaSaver } from "@/hooks/voice/useConversationImageMediaSaver";
import { MAX_IMAGE_ATTACHMENTS, useImageAttachments } from "@/hooks/voice/useImageAttachments";
import { useInConversationCapture } from "@/hooks/voice/useInConversationCapture";
import { useNoiseEnvironment } from "@/hooks/voice/useNoiseEnvironment";
import { useCustomerMemoryConsent } from "@/hooks/voice/useCustomerMemoryConsent";
import { useTextChatSession } from "@/hooks/voice/useTextChatSession";
import { useHumanHandoff } from "@/hooks/voice/useHumanHandoff";
import { useVoiceTranscript } from "@/hooks/voice/useVoiceTranscript";
import { LiveAgentProvider } from "./live-agent/LiveAgentContext";
import { useVoiceAgentConversation } from "@/hooks/voice/useVoiceAgentConversation";
import { useVoiceTroubleSignals } from "@/hooks/voice/useVoiceTroubleSignals";
import { useConversationFollowUp } from "@/hooks/voice/useConversationFollowUp";
import { buildGenieManifest, genieHomeScreenIcon } from "@/lib/agent/genieManifest";
import { hasInChatMedia } from "@/lib/agent/mediaSources";
import { trackEvent, trackEventOnce } from "@/lib/ga4";
import { setLeadConversionSettings } from "@/lib/leadConversions";
import { cn } from "@/lib/utils";
import GenieInvitePage from "@/pages/agent-interfaces/GenieInvitePage";
import GenieAccessRestrictedPage from "@/pages/Errors/GenieAccessRestrictedPage";
import GenieNotFound from "@/pages/Errors/GenieNotFound";
import { activityService } from "@/services/analytics/ActivityService";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import { WidgetCommunicationService } from "@/services/integrations/widget/WidgetCommunicationService";
import { AgentConfig } from "@/types/agent/agent";
import { AgentPageProps } from "@/types/agent/agent-page";
import { InfoLinksConfig } from "@/types/agent/info-links";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { isDarkBackground, widgetBackgroundToCss } from "@/lib/widgetBackgroundPresets";
import EmbedLiveAgentExperience from "./embed/EmbedLiveAgentExperience";
import { useDesktopStageFit } from "./live-agent/useDesktopStageFit";
import { ListenerExperience } from "@/components/recorder/ListenerExperience";
import { ConversationWrapUp } from "./live-agent/components/ConversationWrapUp";
import type { ConnectChannelKey } from "./live-agent/components/ConnectChooser";
import { ConversationAlternativePrompt } from "./live-agent/components/ConversationAlternativePrompt";
import type { TranscriptMessage } from "./live-agent/components/TranscriptPanel";
import { GenieSharedMedia } from "./live-agent/components/GenieAttachmentCard";
import { useFoundMedia } from "@/hooks/voice/useFoundMedia";
import { useLatestAttachments } from "@/hooks/voice/useLatestAttachments";
import { useMediaLookupPending } from "@/hooks/voice/useMediaLookupPending";
import { ConversationFollowUpSheet } from "./live-agent/components/ConversationFollowUpSheet";
import { GenieSupportLink } from "./live-agent/components/GenieSupportLink";
import { GenieCallButton } from "./live-agent/components/GenieCallButton";
import { canDialFromDevice } from "@/lib/tel";
import { STAGE_ANCHOR_CLASS } from "./live-agent/useStageAnchorCentering";
import { CallStage } from "./live-agent/components/CallStage";
import { EmailStage } from "./live-agent/components/EmailStage";
import { GenieChatInputWeb } from "./live-agent/components/GenieChatInputWeb";
import { GenieConversationCanvas } from "./live-agent/components/GenieConversationCanvas";
import { fullPageChatAppearance } from "@/lib/fullPageChatAppearance";
import { GenieDetailsPanel } from "./live-agent/components/GenieDetailsPanel";
import { geniePagePath, isPageLive } from "@/pages/UserHub/GeniePages/geniePageStatus";
import { GenieDetailsLink } from "./live-agent/components/GenieDetailsLink";
import { GenieDetailsTab } from "./live-agent/components/GenieDetailsTab";
import { GenieTextChatOverlayWeb } from "./live-agent/components/GenieTextChatOverlayWeb";
import { GenieWelcomeScreen } from "./live-agent/components/GenieWelcomeScreen";
import { hasAskAlreadyBeenSent, markAskAsSent, parseAskParam } from "./live-agent/askParam";
import { HumanCallAvatar } from "./live-agent/components/HumanCallAvatar";
import { HumanCallBar } from "./live-agent/components/HumanCallBar";
import { HumanHandoffPrompt } from "./live-agent/components/HumanHandoffPrompt";
import { ImageAttachmentPreviewWeb } from "./live-agent/components/ImageAttachmentPreviewWeb";
import { InlineCaptureMoment } from "./live-agent/components/InlineCaptureMoment";
import { MagicalIconButton } from "./live-agent/components/MagicalIconButton";
import { ResourcesRail } from "./live-agent/components/ResourcesRail";
import { ResourcesSheet } from "./live-agent/components/ResourcesSheet";
import { isSafeUrl } from "./live-agent/components/infoLinkIcons";
import { VoiceSessionActionRow } from "./live-agent/components/VoiceSessionActionRow";
import { VoiceTroubleSheet } from "./live-agent/components/VoiceTroubleSheet";
import { VoiceTroubleTrigger } from "./live-agent/components/VoiceTroubleTrigger";
import LiveAgentContainer from "./live-agent/LiveAgentContainer";
import MobileLiveAgentLayout from "./live-agent/mobile/MobileLiveAgentLayout";
import { useRailItems } from "@/hooks/agent/useRailItems";
import { useSurfacedResource } from "@/hooks/agent/useSurfacedResource";
import { useSuggestedResourceMessages } from "@/hooks/agent/useSuggestedResourceMessages";
import type { RailItem } from "@/types/voice/resources-rail";

/**
 * Width of the desktop resources-rail gutter. Applied to the rail column and
 * to the matching spacer on the far side, so the genie column stays optically
 * centred whether or not the transcript is open.
 */
const RAIL_GUTTER_CLASS = "flex-[0_0_272px] self-center";
// Stands where the transcript column would be while it is closed; scrolls on
// its own when the Genie shares more than fits beside the avatar.
const SHARED_MEDIA_COLUMN_CLASS =
  "flex flex-[0_1_400px] min-w-[280px] justify-center self-center max-h-[calc(100dvh-140px)] overflow-y-auto";

/**
 * Bottom text input that stays above the iOS/Android virtual keyboard on mobile.
 */
const MobileAwareBottomInput = ({
  isMobile,
  inlineMessage,
  setInlineMessage,
  onSend,
  attachments,
  removeAttachment,
  updateAttachmentAnalysis,
}: {
  isMobile: boolean;
  inlineMessage: string;
  setInlineMessage: (v: string) => void;
  onSend: () => void;
  attachments: Array<{
    id: string;
    file: File;
    previewUrl: string;
    status: "analyzing" | "ready" | "error";
    analysisText?: string;
  }>;
  removeAttachment: (id: string) => void;
  updateAttachmentAnalysis?: (id: string, text: string) => void;
}) => {
  const prevOverflowRef = useRef<string | null>(null);

  const handleFocusCapture = () => {
    if (!isMobile) return;
    if (prevOverflowRef.current == null) {
      prevOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
  };

  const handleBlurCapture = () => {
    if (!isMobile) return;
    if (prevOverflowRef.current != null) {
      document.body.style.overflow = prevOverflowRef.current;
      prevOverflowRef.current = null;
    }
  };

  return (
    <motion.div
      key="inline-chat-input"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="pointer-events-none flex justify-center px-4"
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
      style={
        isMobile
          ? {
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 12,
              zIndex: 40,
              paddingBottom: "max(0px, env(safe-area-inset-bottom))",
            }
          : {
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 24,
            }
      }
    >
      <div className="pointer-events-auto w-full max-w-md">
        {attachments.length > 0 && (
          <ImageAttachmentPreviewWeb
            attachments={attachments.map((att) => ({
              id: att.id,
              previewUrl: att.previewUrl,
              status: att.status,
              analysisText: att.analysisText,
            }))}
            onRemove={removeAttachment}
            onUpdateAnalysis={updateAttachmentAnalysis}
          />
        )}
        <GenieChatInputWeb
          value={inlineMessage}
          onChange={setInlineMessage}
          onSend={onSend}
          hasAttachments={attachments.length > 0}
          disabled={attachments.some((attachment) => attachment.status === "analyzing")}
          onRemoveAttachment={removeAttachment}
          onOpenFilePicker={() => {
            const input = document.getElementById("genie-chat-attachments") as HTMLInputElement | null;
            if (input) {
              input.click();
            }
          }}
        />
      </div>
    </motion.div>
  );
};

// First-use welcome gate persistence. The "what to expect" screen is meant to
// orient a visitor the first time they open a given Genie — once they've entered
// it on this device, we never show it again for that Genie. Keyed per-Genie so
// each new Genie a visitor tries gets its own one-time welcome. localStorage is
// per-browser (anonymous consumers have no cross-device identity), wrapped so a
// privacy/quota error can never break the page.
const welcomeSeenKey = (genieId: string) => `genie-welcome-seen:${genieId}`;

const hasSeenGenieWelcome = (genieId: string) => {
  try {
    return localStorage.getItem(welcomeSeenKey(genieId)) === "1";
  } catch {
    return false;
  }
};

const markGenieWelcomeSeen = (genieId: string) => {
  try {
    localStorage.setItem(welcomeSeenKey(genieId), "1");
  } catch {
    /* storage unavailable — the welcome simply shows again next visit */
  }
};

function FullPageLiveAgentExperience({
  agentPageData: propAgentData,
  portalMode = false,
  demoMode,
  initialQuestion,
}: AgentPageProps = {}) {
  const INSTALL_BANNER_AFTER_CONVERSATION_DELAY_MS = 2500;
  const { url_name } = useParams<{ url_name: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isInternalAdmin } = useRole();
  const { userProfile } = useAuth();
  const { showAfterConversationOnce } = useInstallAppBanner();
  const memoryConsentMutation = useCustomerMemoryConsent();

  // Read template from URL query params (e.g., ?template=hero)
  const searchParams = new URLSearchParams(window.location.search);
  const templateParam = searchParams.get("template") as "hero" | "sales" | "support" | null;

  // Check if we're in embed mode (either via query param or iframe detection)
  const embedParam = searchParams.get("embed") === "true";
  const isInIframe = window.parent !== window;
  // ?layout=full keeps the standalone live-agent page inside a frame. For a host
  // that gives the genie a whole screen of its own (Gulf Western's MAXX frames
  // RobBot full-screen), the compact widget layout is the wrong shape: small
  // avatar, narrow chat card. Sam, 24 Sep 2026.
  const fullLayoutParam = searchParams.get("layout") === "full";
  const isEmbedMode = !fullLayoutParam && (embedParam || isInIframe);

  // Channel switcher — the top strip's Voice/Chat/Call/Email tabs swap the
  // stage beneath. Deep-links via `?channel=`, written back with
  // history.replaceState so the Genie Page's doors can land a visitor
  // straight into a channel. QR never becomes a channel — it stays a popover.
  const channelParam = searchParams.get("channel");
  // `?ask=` (MAXX's suggested-question deep link) implies the same starting
  // point as `?channel=chat` — skip the voice-first default so the seeded
  // question effect below lands on the typed chat, not the start chooser.
  const askParam = parseAskParam(searchParams.get("ask"));
  const [channel, setChannel] = useState<ConnectChannelKey>(
    channelParam === "chat" || channelParam === "phone" || channelParam === "email"
      ? channelParam
      : askParam
        ? "chat"
        : "voice",
  );
  // Dev-only escape hatch for screenshotting the Call/Email tabs on a Genie
  // with no phone/mailbox/callback wired up yet. Guarded to localhost so it
  // can never leak.
  const debugChannelsForced =
    searchParams.get("debugChannels") === "1" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);

  const { data: fetchedAgentResult, isLoading: isFetching } = useAgentPageByUrlNameFull(url_name);
  const [agent, setAgent] = useState<AgentPageProps["agentPageData"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [privateGenie, setPrivateGenie] = useState<{ genieName: string; passwordProtected: boolean } | null>(null);
  const [showInviteGate, setShowInviteGate] = useState(() => searchParams.get("type") === "invite");
  const pendingQrLogRef = useRef<{ genieId: string; genieName: string; urlName: string } | null>(null);
  const qrLoggedRef = useRef(false);
  const [lastConversationId, setLastConversationId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const stageFit = useDesktopStageFit();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState<string>("");
  const [showTranscript, setShowTranscript] = useState(isEmbedMode);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [fullTextView, setFullTextView] = useState(false);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [conversationStartTime, setConversationStartTime] = useState<number | null>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showResources, setShowResources] = useState(false);
  // Latched true once the current/last conversation ends — triggers a final
  // resources filter pass (see useFilteredResources). Reset when a new
  // conversation starts.
  const [conversationEnded, setConversationEnded] = useState(false);
  // The "poof" Details view (about, links, resources, summary). Reachable from
  // the calm pre/post-conversation start screen via the right-edge tab.
  const [showDetails, setShowDetails] = useState(false);
  // The end-of-conversation wrap-up: recap, soft capture, next steps, app hand-off.
  const [showWrapUp, setShowWrapUp] = useState(false);
  // The "what to expect" welcome gate. Session toggle: stays up until the visitor
  // enters (or until we learn they've already seen it for this Genie).
  const [showWelcome, setShowWelcome] = useState(true);
  // First-use only: if this browser has already entered THIS Genie before, the
  // welcome has done its job — skip it. Derived from agent.id (known by the time
  // we render past the loading gate) so returning visitors get no welcome flash.
  const welcomeSeenForGenie = useMemo(
    () => (agent?.id ? hasSeenGenieWelcome(agent.id) : false),
    [agent?.id],
  );
  const [submittedImageCount, setSubmittedImageCount] = useState(0);
  const { attachments, addFiles, removeAttachment, updateAttachmentAnalysis } =
    useImageAttachments(Math.max(0, MAX_IMAGE_ATTACHMENTS - submittedImageCount));

  // Keep a ref to demoMode so voice hook callbacks always see the latest without causing re-renders
  const demoModeRef = useRef(demoMode);
  demoModeRef.current = demoMode;

  // Live refs so the voice-hook callbacks (created once) always read current values.
  const agentRef = useRef(agent);
  agentRef.current = agent;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const conversationStartTimeRef = useRef(conversationStartTime);
  conversationStartTimeRef.current = conversationStartTime;
  // Connection signals offer an inline alternative; the full sheet is manual.
  const [showVoiceTrouble, setShowVoiceTrouble] = useState(false);
  // The recovery card can be dismissed without interrupting a conversation.
  const [hasVoiceTrouble, setHasVoiceTrouble] = useState(false);
  const expectedEndRef = useRef(false);
  const voiceStartPendingRef = useRef(false);
  const voiceTroubleSignals = useVoiceTroubleSignals();
  useEffect(() => {
    if (voiceTroubleSignals.autoOfferToken > 0) {
      setHasVoiceTrouble(true);
    }
  }, [voiceTroubleSignals.autoOfferToken]);

  // Genie Page hub data — the Genie's mailbox email covers the "Email" channel
  // directly (agent.mailbox), but published guide count is only known via the
  // public hub, so this fetch is the one extra round-trip needed for that number.
  // Fetched lazily — only once the visitor actually opens the resources rail
  // or reaches the wrap-up (which shows the guide count) — not on every page
  // load, since topics/guides/downloads aren't part of the agent payload and
  // most visitors never open either.
  //
  // The exception is a conversation that will end on the Genie Page: the
  // `<Navigate to={helpPageUrl}>` below is a client-side hop, and HelpHubPage
  // reads the same query key, so a payload fetched during the conversation
  // paints there with no spinner. Wrap-up would trigger this same fetch anyway;
  // starting it once the conversation is under way just moves it off the
  // visitor's critical path. Same gates as that navigation, so no surface that
  // never goes there pays for it.
  const hubIsNextPage = !portalMode && !isEmbedMode && !demoMode && !templateParam &&
    !!agent && !agent.redirect_url && isPageLive(agent) &&
    (conversationStartTime !== null || messages.length > 0);
  const { data: helpHubData } = usePublicHelpHub(
    url_name,
    showResources || showWrapUp || hubIsNextPage,
  );

  // Call/Email channel stages — one hook wraps both edge-function invokes
  // (see CallStage / EmailStage below).
  const channelRequests = useChannelRequests({ agentId: agent?.id, urlName: agent?.url_name ?? url_name });
  const callbackAvailable = agent?.outbound_connected === true;

  // True from the moment a human handoff is confirmed until the human call
  // itself ends — suppresses the normal end-of-AI-conversation wrap-up so it
  // doesn't pop up while the Twilio call is starting/in progress.
  // A configured redirect_url that fired while a handoff was in progress —
  // held until the human call ends instead of navigating away mid-call.
  const pendingRedirectUrlRef = useRef<string | null>(null);

  // The standalone post-call flow opens the published support page, with a
  // wrap-up fallback when unavailable. Configured redirects, templated
  // marketing surfaces, the embed popup, and demo cards keep their own exit rules.
  // Hoisted above the handoff hook, which closes over it for the deferred end.
  const shouldUseWrapUp = () => {
    const a = agentRef.current;
    return !isEmbedMode && !demoMode && !templateParam && !a?.redirect_url;
  };

  // Stable reference for demo conversation overrides — prevents voice agent hook from resetting
  const demoConversationOverrides = useMemo(
    () => (demoMode ? { turn: { turn_timeout: demoMode.maxDurationSeconds * 1000 } } : undefined),
    [demoMode?.maxDurationSeconds],
  );

  const voiceAgent = useVoiceAgentConversation(agent?.elevenlabs_id, {
    welcomeMessage: agent?.welcome_message || agent?.page?.welcome_message || "Click to start",
    agentData: propAgentData || agent,
    ...(agent?.userMemory && { userMemory: agent.userMemory }),
    ...(demoConversationOverrides && {
      conversationOverrides: demoConversationOverrides,
    }),
    onRedirect: (redirectUrl) => {
      // Deferred until the human call ends when this end-of-conversation was
      // caused by a handoff — see the handoffInProgressRef effect below.
      if (handoffInProgressRef.current) {
        pendingRedirectUrlRef.current = redirectUrl;
        return;
      }
      handleRedirect(redirectUrl);
    },
    onShowAnalysisLoading: () => {
      setRedirectMessage("Analyzing your conversation...");
      setIsRedirecting(true);
    },
    onConversationEnd: (conversationId) => {
      // Two quick, unexpected reply-less sessions offer inline recovery.
      const durationMs = conversationStartTimeRef.current
        ? Date.now() - conversationStartTimeRef.current
        : null;
      const hadAgentReply = messagesRef.current.some((m) => !m.isUser);
      if (!expectedEndRef.current && !isTextInitiatedRef.current) {
        voiceTroubleSignals.registerConversationEnd(durationMs, hadAgentReply);
      }
      expectedEndRef.current = false;

      setLastConversationId(conversationId);
      // Keep transcript visible and preserve messages after end
      setShowTranscript(true);
      setConversationStartTime(null);
      setConversationEnded(true);
      // When this end was caused by a human handoff, defer the wrap-up until
      // the human call itself ends instead of poofing it up right as the
      // Twilio call is starting.
      if (handoffInProgressRef.current) {
        // no-op: handoffInProgressRef effect below owns the wrap-up trigger
      } else if (shouldUseWrapUp() && messagesRef.current.length > 0) {
        // Open the support page once the session and outstanding saves finish.
        setShowWrapUp(true);
      } else if (!isEmbedMode) {
        // Skip the install-app banner inside the embedded widget — there's no
        // room, and a website visitor isn't an "install the app" audience anyway.
        window.setTimeout(() => {
          showAfterConversationOnce();
        }, INSTALL_BANNER_AFTER_CONVERSATION_DELAY_MS);
      }
      demoModeRef.current?.onConversationEnd?.();
    },
  });

  const conversationId = voiceAgent.getConversationId();
  const imageConversationIdRef = useRef<string | null>(null);
  const {
    stageImages,
    isSaving: isSavingImages,
    pendingCount: pendingImageUploads,
    failedCount: failedImageUploads,
    retryFailed: retryFailedImageUploads,
    settleUploads: settleImageUploads,
  } = useConversationImageMediaSaver({
    conversationId,
    userId: userProfile?.id ?? null,
  });

  useEffect(() => {
    if (conversationId && imageConversationIdRef.current && imageConversationIdRef.current !== conversationId) {
      setSubmittedImageCount(0);
    }
    if (conversationId) imageConversationIdRef.current = conversationId;
  }, [conversationId]);

  const endConversationAfterUploads = useCallback(async () => {
    const settled = await settleImageUploads(10_000);
    if (!settled) toast.error("Some photos are still saving. They will remain available to retry.");
    expectedEndRef.current = true;
    await voiceAgent.endConversation();
    setSubmittedImageCount(0);
  }, [settleImageUploads, voiceAgent]);

  // Fires once per session the moment the trouble-call chip actually renders.
  useEffect(() => {
    if (voiceAgent.connected && hasVoiceTrouble && agent?.phone?.number) {
      trackEventOnce("live_agent_trouble_call_shown", "live_agent_trouble_call_shown", {
        agent_id: agent?.id ?? undefined,
      });
    }
  }, [voiceAgent.connected, hasVoiceTrouble, agent?.phone?.number, agent?.id]);

  const canManageCustomerMemory =
    userProfile?.role === "consumer" && agent?.memory_enabled === true && !!agent?.id;

  const updateCustomerMemoryConsent = async (enabled: boolean) => {
    if (!canManageCustomerMemory || !agent?.id) return;
    try {
      const result = await memoryConsentMutation.mutateAsync({ genieId: agent.id, enabled });
      setAgent((current) =>
        current
          ? {
              ...current,
              memoryConsent: result.memoryConsent,
              userMemory: enabled ? current.userMemory : null,
            }
          : current,
      );
      if (!enabled) voiceAgent.getService()?.setUserMemory(null);
    } catch (consentError) {
      console.error("Unable to update conversation memory preference:", consentError);
      toast.error("We couldn't update your memory preference. This conversation won't be remembered.");
    }
  };

  // Whether the agent echoes user transcripts in client_events (drives whether
  // the transcript panel is worth auto-opening).
  const transcriptFeatureEnabled = Boolean(
    (agent?.settings as AgentConfig | undefined)?.conversation_config?.conversation?.client_events?.includes(
      "user_transcript",
    ),
  );

  // Split audio controls (mic / agent voice / phone-style pause hold), shared
  // verbatim with the embedded experience. Handler aliases keep the existing
  // JSX prop names working unchanged.
  const {
    isMicMuted,
    setIsMicMuted,
    isAgentVoiceMuted,
    setIsAgentVoiceMuted,
    isPaused,
    isAllAudioMuted,
    toggleMicMute: handleToggleMicMute,
    toggleAgentVoice: handleToggleAgentVoice,
    togglePause: handleTogglePause,
    toggleAllAudioMute: handleToggleAllAudioMute,
  } = useAudioSessionControls({
    voiceAgent,
    onAgentVoiceMuted: () => {
      // Voice muted → open the transcript so the reply stays visible.
      if (transcriptFeatureEnabled) setShowTranscript(true);
    },
  });

  // Text chat: composer value, text-only/started-from-text mode, the
  // optimistic send flow, and ending a text chat. Shared with the embed.
  const {
    inlineMessage,
    setInlineMessage,
    isTextOnlyMode,
    setIsTextOnlyMode,
    startedFromText,
    isTextInitiatedRef,
    optimisticUserMessagesRef,
    beginTextSession,
    sendInlineMessage: handleInlineSend,
    endTextChat: handleEndTextChat,
  } = useTextChatSession({
    voiceAgent,
    audio: { setIsMicMuted, setIsAgentVoiceMuted },
    attachments,
    removeAttachment,
    stageImages,
    endConversationAfterUploads,
    setMessages,
    setShowTranscript,
    setConversationStartTime,
    clientEvents: (agent?.settings as AgentConfig | undefined)?.conversation_config?.conversation
      ?.client_events,
    onImagesStaged: (count) => setSubmittedImageCount((current) => current + count),
  });

  const { isNoisy: showNoiseWarning, dismiss: dismissNoiseWarning } = useNoiseEnvironment({
    getService: voiceAgent.getService,
    enabled: voiceAgent.connected && !isTextOnlyMode && !startedFromText,
    isAgentSpeaking: voiceAgent.speaking,
  });

  // Fire demo onConversationStart when connected
  useEffect(() => {
    if (voiceAgent.connected) {
      demoModeRef.current?.onConversationStart?.();
    }
  }, [voiceAgent.connected]);

  // Auto-open the chat/transcript panel the moment voice connects so users
  // can both speak and see the transcript without an extra click. Text-only
  // starts already open the panel in handleInlineSend, so we skip there.
  // In the embedded widget we open even when the agent didn't enable user
  // transcripts in client_events — the popup is one surface, hiding the chat
  // would force users back to "voice only" which defeats the type-or-talk UX.
  useEffect(() => {
    if (voiceAgent.connected && !startedFromText && (transcriptFeatureEnabled || isEmbedMode)) {
      setShowTranscript(true);
    }
  }, [voiceAgent.connected, startedFromText, isEmbedMode]);

  // Text-mode and audio flags reset on disconnect inside their hooks; only the
  // desktop chat-expanded layout flag still lives here.
  useEffect(() => {
    if (!voiceAgent.connected) {
      setChatExpanded(false);
    }
  }, [voiceAgent.connected]);

  // Chat tab (mobile) — the transcript drawer is the "text chat card" on
  // mobile (see MobileLiveAgentLayout), so selecting Chat just opens it,
  // same as the old "Type instead" chip. Re-fires once a chat session ends
  // (isTextOnlyMode flips back to false) so ending a chat lands back on the
  // empty, ready-to-type stage instead of falling back to voice.
  useEffect(() => {
    if (agent?.id && channel === "chat" && isMobile && !voiceAgent.connected && !isTextOnlyMode && !showWrapUp) {
      setShowTranscript(true);
    }
  }, [agent?.id, channel, isMobile, voiceAgent.connected, isTextOnlyMode, showWrapUp]);

  // Keep the URL's `?channel=` in sync so the current tab is a shareable
  // deep link (the Genie Page's doors land straight into a channel). Skipped
  // in the embed popup / demo card, where the URL isn't the visitor-facing one.
  useEffect(() => {
    if (isEmbedMode || demoMode) return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get("channel");
    if (channel === "voice") {
      if (current === null) return;
      url.searchParams.delete("channel");
    } else {
      if (current === channel) return;
      url.searchParams.set("channel", channel);
    }
    window.history.replaceState({}, "", url.toString());
  }, [channel, isEmbedMode, demoMode]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // Agent loading effect - use prop data if available, otherwise use fetched data
  useEffect(() => {
    if (propAgentData) return;
    setAgent(null);
    setError(null);
    setPrivateGenie(null);
    setMessages([]);
    setShowTranscript(false);
    setShowDetails(false);
    setFullTextView(false);
    setShowWrapUp(false);
    setShowWelcome(true);
    setHasVoiceTrouble(false);
    setShowVoiceTrouble(false);
    setLastConversationId(null);
    setSubmittedImageCount(0);
    seededQuestionRef.current = false;
    qrLoggedRef.current = false;
    pendingQrLogRef.current = null;
  }, [url_name, propAgentData]);

  useEffect(() => {
    if (propAgentData) {
      setAgent(propAgentData);
      return;
    }

    if (isFetching) return;

    if (!fetchedAgentResult) {
      setError("Agent not found");
      return;
    }

    if (fetchedAgentResult.status === "private") {
      setPrivateGenie({
        genieName: fetchedAgentResult.genieName,
        passwordProtected: fetchedAgentResult.passwordProtected,
      });
      return;
    }

    if (fetchedAgentResult.status === "ok") {
      setPrivateGenie(null);
      setError(null);
      setAgent(fetchedAgentResult.agent);
    } else {
      setError("Agent not found");
    }
  }, [propAgentData, fetchedAgentResult, isFetching]);

  // Fire GA4 demo_started once per session when the Genie demo page successfully resolves an agent.
  // `demo_start` is the Google Ads/GA4 key event this maps to — kept alongside
  // `demo_started` so nothing that already reports on the old name breaks.
  useEffect(() => {
    if (!agent) return;
    const genieId = agent.agents?.id ?? agent.id ?? "";
    const urlName = agent.url_name ?? "";
    const params = {
      genie_id: genieId,
      url_name: urlName,
      surface: isEmbedMode ? "embed" : "live_agent",
    };
    trackEventOnce(`demo_started:${urlName || genieId}`, "demo_started", params);
    trackEventOnce(`demo_start:${urlName || genieId}`, "demo_start", params);
  }, [agent?.id, isEmbedMode]);

  // Track QR code scans — fires once when agent resolves and ?ref=qr is present.
  // When ?type=invite is also present, defers until the invite gate resolves so consumerId can be attached.
  useEffect(() => {
    if (!agent) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("ref") !== "qr") return;
    const genieId = agent.id;
    const genieName = agent.name ?? "";
    if (!genieId) return;
    if (showInviteGate) {
      pendingQrLogRef.current = { genieId: genieId!, genieName, urlName: agent.url_name ?? "" };
      return;
    }
    if (qrLoggedRef.current) return;
    qrLoggedRef.current = true;
    activityService.logQrScanned(genieId!, genieName, agent.url_name ?? "");
  }, [agent?.id, showInviteGate]);

  // Transcript: subscribe to the voice message stream, appending each entry
  // (skipping the optimistic-echo of typed messages) and persisting to
  // localStorage keyed by conversation id. Shared with the embed.
  useVoiceTranscript({
    voiceAgent,
    setMessages,
    optimisticUserMessagesRef,
    conversationStartTimeRef,
  });

  // Embed mode communication - notify parent widget when ready
  useEffect(() => {
    if (isEmbedMode) {
      // Send ready message to parent widget
      WidgetCommunicationService.sendToParent("helpgenie:ready", { agentUrl: url_name });
    }
  }, [isEmbedMode, url_name]);

  // Notify parent when conversation state changes (for embed mode)
  useEffect(() => {
    if (isEmbedMode && voiceAgent.connected) {
      WidgetCommunicationService.sendToParent("helpgenie:conversation:started", {
        agentId: agent?.elevenlabs_id,
      });
    }
  }, [isEmbedMode, voiceAgent.connected, agent?.elevenlabs_id]);

  // Hide the public Help Genie widget bubble while on this page — it overlaps
  // the voice UI and doesn't belong on agent-facing surfaces.
  useEffect(() => {
    const v1 = document.getElementById('helpgenie-widget-container');
    const v2 = document.querySelector<HTMLElement>('.hgv2-host');
    if (v1) v1.style.display = 'none';
    if (v2) v2.style.display = 'none';
    return () => {
      if (v1) v1.style.display = '';
      if (v2) v2.style.display = '';
    };
  }, []);

  // Suppress iframe document scrollbars in embed mode. The widget popup has its
  // own fixed dimensions; any overflow inside should be clipped, not scrolled.
  useEffect(() => {
    if (!isEmbedMode) return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isEmbedMode]);

  // Live voice handoff to a human, via Twilio Voice JS SDK — fully additive,
  // dormant unless agent.voice_bridge_enabled is set (default false). AI client
  // tool + manual button both call confirmHandoff. Shared with the embed; the
  // deferred-end callback below is what runs the wrap-up/redirect the
  // AI session held back while the Twilio call started.
  const {
    voiceBridge,
    showPrompt: showHandoffPrompt,
    openPrompt: openHandoffPrompt,
    closePrompt: closeHandoffPrompt,
    error: handoffError,
    showHandoffButton: showHumanHandoffButton,
    handoffInProgressRef,
    deferredActionsRef: handoffDeferredActionsRef,
    confirmHandoff: handleConfirmHumanHandoff,
    hangupHandoff: handleHangupHumanHandoff,
  } = useHumanHandoff({
    voiceAgent,
    agentId: agent?.id,
    enabled: Boolean(agent?.voice_bridge_enabled),
    showButton: agent?.voice_bridge_show_button !== false,
    onDeferredEnd: (actions) => {
      if (pendingRedirectUrlRef.current) {
        const redirectUrl = pendingRedirectUrlRef.current;
        pendingRedirectUrlRef.current = null;
        actions.redirect(redirectUrl);
      } else {
        actions.wrapUp();
      }
    },
  });
  // Rebuilt every render so the hook always calls the latest closures.
  handoffDeferredActionsRef.current = {
    redirect: (url) => handleRedirect(url),
    wrapUp: () => {
      if (shouldUseWrapUp() && messagesRef.current.length > 0) setShowWrapUp(true);
    },
  };
  const isVoiceBridgeEnabled = Boolean(agent?.voice_bridge_enabled);

  const brand = agent?.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const websiteChatAppearance = isEmbedMode || demoMode ? undefined : fullPageChatAppearance(
    agent?.branding?.embedStyle,
    agent?.branding?.logoUrl || agent?.page?.genie_image_url,
  );

  // When a visitor leaves their email (via the auto-pill or the "Get a summary"
  // chip), quietly let the Genie know so it doesn't ask for it again.
  const handleContactCaptured = useCallback(
    (email: string) => {
      voiceAgent.sendContextualUpdate(
        `[SYSTEM CONTEXT - DO NOT READ ALOUD]\nThe user just provided their email: ${email}`,
      );
    },
    [voiceAgent],
  );

  // Branding + agent-identity bundle published to every live-agent surface via
  // LiveAgentContext, so the shared components don't each re-receive it by hand.
  const liveAgentProviderProps = {
    brandColor: brand,
    secondaryColor: agent?.branding?.secondaryColor,
    agentName: agent?.name,
    agentId: agent?.id,
    agentUserId: agent?.user_id,
    qrLogoUrl: agent?.branding?.logoUrl || null,
    logoUrl: agent?.branding?.logoUrl || null,
    onContactCaptured: handleContactCaptured,
  };

  // Any touch on the screen during a conversation reactivates wake lock strategies
  // This is critical for iOS Safari where strategies can be silently killed
  const handleScreenTouch = useCallback(() => {
    if (voiceAgent.connected && voiceAgent.wakeLock?.reactivateAll) {
      voiceAgent.wakeLock.reactivateAll();
    }
  }, [voiceAgent.connected, voiceAgent.wakeLock]);

  // Resources rail — hooks live above the early returns below. The rail itself
  // renders inside the layout, but its item list and "the Genie just named
  // this" highlight have to be derived unconditionally.
  const rawResourcesConfig = (agent?.page?.info_links as InfoLinksConfig | null) ?? null;
  // AI filtering only fires ~5s into the conversation, when the Resources
  // sheet opens, or when the conversation ends — never continuously. Until
  // the first pass resolves (for a genie with more resources than fit on
  // screen), this is null and the rail stays hidden rather than showing an
  // unfiltered guess — see useFilteredResources.
  const inChatMedia = hasInChatMedia(agent?.settings as AgentConfig | undefined);
  const resourcesConfig = useFilteredResources(
    conversationId,
    rawResourcesConfig,
    voiceAgent.connected,
    showResources,
    conversationEnded,
    messages,
    { inChatMedia },
  );
  // A genie that puts links in the chat itself has no rail: the cards are the
  // same links, shown better. The Resources sheet stays.
  const railItems = useRailItems(inChatMedia ? null : resourcesConfig);
  const surfacedResourceId = useSurfacedResource(messages, railItems);
  // With the chat on screen and no gutter for the rail, the suggested
  // resources sit in the chat itself as cards.
  const chatMessages = useSuggestedResourceMessages(messages, railItems, voiceAgent.connected && !demoMode);

  // In-conversation capture moment — arms after a few user turns or enough
  // elapsed time (see the hook), and renders inline in whichever transcript
  // surface is active (mobile drawer, desktop overlay, or the text-start
  // canvas).
  const inConversationCapture = useInConversationCapture({
    connected: voiceAgent.connected && (isEmbedMode || !!demoMode),
    messages,
    conversationStartTime,
    agentId: agent?.id,
    agentUserId: agent?.user_id,
    agentBusy: voiceAgent.speaking || voiceAgent.thinking || voiceAgent.processing || voiceAgent.searching || voiceAgent.retrieving,
    // Nothing goes to the Genie when this ask is answered: it is a side note,
    // not part of the conversation (Sam, 24 Sep 2026). The email is saved with
    // the conversation id instead, which is how the recap finds it.
    conversationId: conversationId || lastConversationId,
  });

  // A genie counting chat leads as ad conversions carries requestContactDetails;
  // its call opens this same moment as a contact ask.
  const leadConversions = agent?.branding?.embedStyle?.leadConversions;
  useEffect(() => {
    setLeadConversionSettings(leadConversions);
  }, [leadConversions]);

  const { requestContactDetails } = inConversationCapture;
  const { onContactDetailsRequested } = voiceAgent;
  useEffect(() => {
    onContactDetailsRequested(requestContactDetails);
    return () => onContactDetailsRequested(null);
  }, [onContactDetailsRequested, requestContactDetails]);

  const latestShare = useLatestAttachments(messages);
  const mediaPending = useMediaLookupPending();
  const hasFoundMedia = useFoundMedia().length > 0;

  const canEmailTeam =!isEmbedMode && !demoMode && !!agent?.url_name && (agent.is_public === true || agent.is_marketplace_genie === true);
  const followUp = useConversationFollowUp({
    agentId: agent?.id,
    initialEmail: userProfile?.email,
    conversationId: conversationId || lastConversationId,
    sessionKey: conversationId || lastConversationId,
    messages,
    enabled: canEmailTeam,
    pageUrl: `${window.location.origin}/live-agent/${agent?.url_name ?? url_name}`,
    askQuestion: channelRequests.askQuestion,
    onCaptured: handleContactCaptured,
  });

  // Cleanup is handled by useVoiceAgentConversation hook

  const flushPendingQrLog = (consumerId?: string | null) => {
    if (!pendingQrLogRef.current || qrLoggedRef.current) return;
    qrLoggedRef.current = true;
    const { genieId, genieName, urlName: qrUrlName } = pendingQrLogRef.current;
    pendingQrLogRef.current = null;
    activityService.logQrScanned(genieId, genieName, qrUrlName, consumerId);
  };

  // The genie's own branding first; the old page copy only as a fallback.
  const agentLogo = agent?.branding?.logoUrl || agent?.page?.genie_image_url;

  useEffect(() => {
    if (!agentLogo) return;
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    const prev = link?.href;
    if (link) link.href = agentLogo;
    return () => {
      if (link && prev) link.href = prev;
    };
  }, [agentLogo]);

  // A genie's own page can be pinned to a phone's home screen. Not inside an
  // embed, demo card or portal (the host page isn't the genie's), nor on the
  // legacy /agent/ route. ?layout=full still counts as embedded here.
  const homeScreenUrlName = agent?.url_name ?? url_name;
  const homeScreenEnabled =
    !isEmbedMode && !embedParam && !fullLayoutParam && !isInIframe && !demoMode && !portalMode && !pathname.startsWith("/agent/") && !!agent?.name && !!homeScreenUrlName;
  const homeScreenIcon = genieHomeScreenIcon(window.location.origin, agentLogo);
  const [homeScreenManifestUrl, setHomeScreenManifestUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!homeScreenEnabled || !agent?.name || !homeScreenUrlName) return;
    const manifest = buildGenieManifest({
      name: agent.name,
      urlName: homeScreenUrlName,
      origin: window.location.origin,
      themeColor: brand,
      logoUrl: agentLogo,
    });
    const json = JSON.stringify(manifest);
    // Blob URL where available (it carries the page's origin); data URL otherwise.
    const isBlob = typeof URL.createObjectURL === "function";
    const url = isBlob
      ? URL.createObjectURL(new Blob([json], { type: "application/manifest+json" }))
      : `data:application/manifest+json,${encodeURIComponent(json)}`;
    setHomeScreenManifestUrl(url);
    return () => {
      if (isBlob) URL.revokeObjectURL(url);
      setHomeScreenManifestUrl(null);
    };
  }, [homeScreenEnabled, agent?.name, homeScreenUrlName, brand, agentLogo]);

  const handleRailSelect = useCallback(
    (item: RailItem) => {
      trackEvent("resource_opened", {
        resource_id: item.id,
        resource_kind: item.kind,
        resource_label: item.label,
        surface: "rail",
        agent_id: agent?.id ?? undefined,
      });
    },
    [agent?.id],
  );

  const handleRailImpression = useCallback(
    (item: RailItem) => {
      trackEventOnce(`rail_impression_${item.id}`, "resource_impression", {
        resource_id: item.id,
        resource_kind: item.kind,
        surface: "rail",
        agent_id: agent?.id ?? undefined,
      });
    },
    [agent?.id],
  );

  // Seeded first question — either a Genie Page question chip (initialQuestion
  // prop) or MAXX's `?ask=` deep link (askParam). `ask` wins when both are
  // somehow present: it is the explicit URL contract, the prop is not used by
  // this route at all. This must stay above every render gate so loading the
  // agent cannot change the component's hook order.
  const seededQuestionRef = useRef(false);
  useEffect(() => {
    const question = (askParam ?? initialQuestion)?.trim();
    if (!question || seededQuestionRef.current || !agent) return;
    if (!voiceAgent.getService()) return;

    const resolvedUrlName = agent.url_name ?? url_name ?? "";
    // `ask` survives a plain reload of the same URL (unlike initialQuestion,
    // which an in-page caller passes fresh each time) — guard with
    // sessionStorage so reloading the same MAXX link doesn't ask again into a
    // brand-new conversation.
    if (askParam && hasAskAlreadyBeenSent(resolvedUrlName, askParam)) {
      seededQuestionRef.current = true;
      return;
    }

    seededQuestionRef.current = true;
    if (askParam) markAskAsSent(resolvedUrlName, askParam);
    setShowWelcome(false);
    void handleInlineSend(question);
    // handleInlineSend is recreated every render; the ref guard is what makes
    // this run once, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, askParam, initialQuestion, voiceAgent, url_name]);

  if (!agent && isFetching && !propAgentData) {
    return <BrandSpinner label="Awakening your Genie…" />;
  }
  if (privateGenie) {
    return (
      <GenieAccessRestrictedPage
        urlName={url_name ?? ""}
        genieName={privateGenie.genieName}
        passwordProtected={privateGenie.passwordProtected}
      />
    );
  }
  if (agent && showInviteGate) {
    return (
      <GenieInvitePage
        urlName={url_name ?? ""}
        genieName={agent.name ?? ""}
        agentId={agent.id ?? ""}
        branding={agent.branding}
        userEmail={userProfile?.email}
        onSkip={() => {
          flushPendingQrLog();
          setShowInviteGate(false);
        }}
        onSuccess={(consumerId) => {
          flushPendingQrLog(consumerId);
          setShowInviteGate(false);
        }}
      />
    );
  }
  if (error || !agent) {
    const isInactive = error?.includes?.("Genie Not Available") ?? false;
    return <GenieNotFound isInactive={isInactive} />;
  }

  if (agent.type === "listener") {
    return <ListenerExperience agent={agent} />;
  }

  // The tenant's own contact number, when this genie has one provisioned.
  // Generic lookup only — no HG-specific or hardcoded number, ever.
  const phoneNumber = agent?.phone?.number || null;
  // Genie mailbox email — already on the loaded agent, so this needs no extra
  // fetch. Guide count is only known via the public hub (fetched above).
  const emailAddress = agent?.mailbox?.email_address || null;
  const guideCount = helpHubData?.guideCount;
  // Same-origin URL used by the Call/Email stages' edge-function calls. Falls
  // back to the route param only when agent data was fetched by url_name —
  // callers that preload agentPageData (portal mode, /demo/:token) have no
  // matching :url_name route param, so agent.url_name is the only value that
  // resolves in that case.
  const channelPageUrl = `${window.location.origin}/live-agent/${agent?.url_name ?? url_name}`;
  const followUpPrompt = followUp.available && !followUp.dismissed ? (
    <ConversationAlternativePrompt kind="email" onDismiss={followUp.dismiss} onEmail={followUp.openSheet} />
  ) : null;
  const inlineCaptureNode = followUp.showPrompt ? followUpPrompt : inConversationCapture.visible ? (
    <InlineCaptureMoment
      brandColor={brand}
      secondaryColor={agent?.branding?.secondaryColor}
      agentName={agent?.name}
      contactRequest={inConversationCapture.contactRequested}
      values={inConversationCapture.capture.values}
      onChange={inConversationCapture.capture.onChange}
      onSubmit={inConversationCapture.capture.submit}
      isSubmitting={inConversationCapture.capture.isSubmitting}
      submitted={inConversationCapture.capture.submitted}
      error={inConversationCapture.capture.error}
      onDismiss={inConversationCapture.dismiss}
      alreadyCapturedEmail={
        inConversationCapture.capture.alreadyCaptured && !inConversationCapture.capture.editing
          ? inConversationCapture.capture.capturedEmail
          : null
      }
      onChangeEmail={inConversationCapture.capture.startEdit}
    />
  ) : null;
  if (isRedirecting) {
    return <RedirectingScreen brandColor={brand} message={redirectMessage || "Redirecting..."} />;
  }

  // Widget popup color overrides. This file's embed-mode branches (mobile and
  // desktop) render the same popup as EmbedLiveAgentExperience for viewports
  // wide enough to skip that early-handoff path, so the CTA/action-button
  // overrides apply only when isEmbedMode is true — the standalone live-agent
  // page is unaffected, matching the existing `gradient={isEmbedMode}` pattern.
  const embedCtaStyle = isEmbedMode ? agent?.branding?.embedStyle?.ctaButton : null;
  const embedCtaShape = isEmbedMode ? agent?.branding?.embedStyle?.buttonStyle : undefined;
  const embedActionButtonStyle = isEmbedMode ? agent?.branding?.embedStyle?.actionButtons : null;
  const embedAvatarImageUrl = isEmbedMode ? agent?.branding?.embedStyle?.avatarImageUrl : null;
  const embedAvatarShowLogoOverlay = isEmbedMode ? agent?.branding?.embedStyle?.avatarShowLogoOverlay : false;
  const embedAvatarSizeScale = isEmbedMode ? agent?.branding?.embedStyle?.avatarSizeScale : undefined;

  const hasResources = !!(
    rawResourcesConfig &&
    ((rawResourcesConfig.documents?.length ?? 0) > 0 || (rawResourcesConfig.links?.length ?? 0) > 0)
  ) || hasFoundMedia;

  // The Genie Page is this genie's hub — guides, FAQs and every way in. Offer
  // it from the details panel whenever it's live, except in portal mode, where
  // the visitor is already inside that very page.
  const helpPageUrl = !portalMode && agent && isPageLive(agent) ? geniePagePath(agent) : null;

  // The Details tab + poof view are a mid-conversation affordance only. Before
  // a conversation, More and the support-page link cover alternate channels,
  // so the floating tab stays
  // hidden until a call is actually live. Excluded also from the embed
  // popup, the marketing demo card, and behind an open mobile drawer.
  const showDetailsAffordance =
    voiceAgent.connected &&
    !isEmbedMode &&
    !demoMode &&
    !(isMobile && mobileDrawerOpen);

  // The "Having trouble?" fallback only makes sense on the live voice stage —
  // once a visitor is already in text (chat panel open) they have already
  // self-served the fallback this offers.
  const showVoiceTroubleTrigger =
    voiceAgent.connected &&
    !isTextOnlyMode &&
    !showTranscript &&
    !isPaused &&
    !showWrapUp &&
    !showDetails &&
    !isEmbedMode &&
    !demoMode &&
    !(isMobile && mobileDrawerOpen);

  // The transcript already owns the right column, so the rail takes the left
  // gutter. Both gutters stay reserved even when the transcript is closed, so
  // opening the chat never shifts the orb or moves the rail. Framed or not,
  // the gutter only shows while the viewport has room for it (see
  // useDesktopStageFit); otherwise the rail moves into the genie or chat column.
  const railInGutter = railItems.length > 0 && !demoMode;

  // Text starts and the voice transcript's Full text view action share the
  // desktop conversation canvas. This changes presentation only: the session,
  // audio preferences, messages and composer stay owned by this page.
  //
  // The Chat tab (`channel === "chat"`) also always gets the canvas, even
  // before a message is sent — previously the pre-send state rendered a small
  // "ready to type" card in the voice column instead (Sam, 29 Sep 2026: "I
  // don't like this screen at all. When we click into chat it should always
  // be the full immersive version"). The canvas already has its own empty
  // state (ConversationWelcome), so there is nothing to gate on
  // showTranscript/transcriptFeatureEnabled until a session is actually live.
  const conversationCanvasActive =
    !isMobile &&
    !isEmbedMode &&
    !demoMode &&
    (channel === "chat" ||
      ((startedFromText || fullTextView) &&
        showTranscript && (transcriptFeatureEnabled || isTextOnlyMode)));

  const agentName = agent?.name;
  const agentDescription = agent?.description;
  const pageTitle = agentName ? `${agentName}` : "Live Agent";
  // Same fallback reasoning as channelPageUrl above — agent.url_name covers
  // preloaded-agentPageData callers with no matching :url_name route param.
  const shareUrl = `${window.location.origin}/live-agent/${agent?.url_name ?? url_name}`;

  const helmet = (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={agentDescription || ""} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={agentDescription || ""} />
      <meta property="og:url" content={shareUrl} />
      <meta property="og:type" content="website" />
      {agentLogo && <meta property="og:image" content={agentLogo} />}
      {homeScreenManifestUrl && <link rel="manifest" href={homeScreenManifestUrl} />}
      {homeScreenEnabled && <link rel="apple-touch-icon" href={homeScreenIcon} />}
      {homeScreenEnabled && <meta name="apple-mobile-web-app-capable" content="yes" />}
      {homeScreenEnabled && <meta name="mobile-web-app-capable" content="yes" />}
      {homeScreenEnabled && <meta name="apple-mobile-web-app-title" content={agentName} />}
    </Helmet>
  );

  const handleShare = async () => {
    try {
      const shareData = {
        title: agentName ? `${agentName} — Live Agent` : "Live Agent",
        text: agentDescription || "Talk to my Genie live:",
        url: shareUrl,
      };
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied to clipboard");
      } catch {
        toast.error("Unable to share. Please copy the URL from the address bar.");
      }
    }
  };

  const handleAvatarClick = async () => {
    if (voiceAgent.isConnecting || voiceStartPendingRef.current) return;
    // Track conversation start time and clear previous transcript when starting
    if (!voiceAgent.connected && !voiceAgent.isConnecting) {
      setConversationStartTime(Date.now());
      setMessages([]);
      setHasVoiceTrouble(false);
      setConversationEnded(false);
      expectedEndRef.current = false;
    } else if (voiceAgent.connected) {
      // If ending conversation and agent has redirect URL, show analyzing screen immediately
      if (agent?.redirect_url) {
        setRedirectMessage("Redirecting...");
        setIsRedirecting(true);
      }
    }

    if (voiceAgent.connected) {
      await endConversationAfterUploads();
      return;
    }
    voiceStartPendingRef.current = true;
    try {
      const started = await voiceAgent.startConversation();
      if (!started) {
        setHasVoiceTrouble(true);
        setConversationStartTime(null);
      }
    } finally {
      voiceStartPendingRef.current = false;
    }
  };

  const handleRedirect = (redirectUrl: string) => {
    setRedirectMessage("Redirecting...");
    setIsRedirecting(true);

    // Show redirecting screen for a bit before redirecting
    setTimeout(() => {
      // Validate URL and redirect
      try {
        if (!isSafeUrl(redirectUrl)) throw new Error("Unsupported redirect protocol");
        const url = new URL(redirectUrl, window.location.origin);
        window.location.href = url.toString();
      } catch (error) {
        console.error("Invalid redirect URL:", redirectUrl);
        setIsRedirecting(false);
      }
    }, 200);
  };

  const handleStartOver = () => {
    setFullTextView(false);
    setMessages([]);
    setShowTranscript(false);
    setConversationStartTime(null);
  };

  /**
   * Open a picker from the action row. `addFiles` starts the vision analysis
   * straight away, and the composer below reveals itself as soon as there is an
   * attachment, so this works with no chat open and no voice session running.
   */
  const handleAddImage = (source: "library" | "camera") => {
    const id = source === "camera" ? "genie-chat-camera" : "genie-chat-attachments";
    (document.getElementById(id) as HTMLInputElement | null)?.click();
  };

  // Welcome gate → voice. Dismiss the gate, remember it for this Genie so it's
  // first-use only, then run the normal start path.
  const handleStartVoiceFromWelcome = async () => {
    if (memoryConsentMutation.isPending) return;
    if (canManageCustomerMemory && agent?.memoryConsent === "unset") {
      await updateCustomerMemoryConsent(false);
    }
    if (agent?.id) markGenieWelcomeSeen(agent.id);
    setShowWelcome(false);
    void handleAvatarClick();
  };

  // Welcome gate → text chat. Starts a text-only session so a visitor can land
  // straight in text mode without a first message.
  const handleStartTextFromWelcome = async () => {
    if (memoryConsentMutation.isPending) return;
    if (canManageCustomerMemory && agent?.memoryConsent === "unset") {
      await updateCustomerMemoryConsent(false);
    }
    if (agent?.id) markGenieWelcomeSeen(agent.id);
    setShowWelcome(false);
    if (voiceAgent.connected || voiceAgent.isConnecting) return;
    await beginTextSession();
  };

  const handleNoiseSwitchToText = async () => {
    dismissNoiseWarning();
    setHasVoiceTrouble(false);
    setShowVoiceTrouble(false);
    setShowWrapUp(false);
    try {
      if (voiceAgent.connected) {
        // Keep the live conversation and its context; only change the interface.
        const service = voiceAgent.getService();
        await service?.setMicMuted(true);
        await service?.setVolume(0);
        setIsMicMuted(true);
        setIsAgentVoiceMuted(true);
        setIsTextOnlyMode(true);
        setShowTranscript(true);
        return;
      }
      setChannel("chat");
      const started = await beginTextSession();
      if (!started) {
        toast.error("Could not switch to text chat. Please try again.");
      }
    } catch (e) {
      console.error("Error switching to text from noise hint", e);
      toast.error("Could not switch to text chat. Please try again.");
    }
  };

  // Status message is handled by the hook — suppressed during a live human
  // call since HumanCallBar shows its own status right below the avatar.
  const getStatusMessage = () =>
    isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" ? "" : voiceAgent.getStatusMessage();

  const handleOpenChat = () => {
    setIsTextOnlyMode(true);
    setShowTranscript(true);
    if (!voiceAgent.connected) setChannel("chat");
  };

  const consolidatedSession = !demoMode && voiceAgent.connected;
  const chatPanelOpen = isMobile ? mobileDrawerOpen : showTranscript;
  // Human handoff is a More-menu action on every surface, never a button of
  // its own on the stage.
  const onTalkToHuman =
    showHumanHandoffButton && voiceBridge.bridgeStatus === "idle" ? openHandoffPrompt : undefined;
  const contactActions = !demoMode ? {
    onTalkToHuman,
    onEndConversation: () => void endConversationAfterUploads(),
    chatOpen: chatPanelOpen,
    onCallGenie: phoneNumber || callbackAvailable
      ? () => voiceAgent.connected ? setShowVoiceTrouble(true) : setChannel("phone")
      : undefined,
    onEmailGenie: followUp.available ? followUp.openSheet : canEmailTeam ? () => setChannel("email") : undefined,
    emailActionLabel: followUp.available ? "Continue over email" : "Email the team",
    addToHomeScreen: homeScreenEnabled ? { genieName: agentName ?? "", iconUrl: homeScreenIcon } : undefined,
  } : { onTalkToHuman };
  const followUpSheet = (
    <ConversationFollowUpSheet open={followUp.open} onClose={followUp.close} form={followUp.form}
      purpose={followUp.purpose} contextTruncated={followUp.contextTruncated}
      onSubmit={followUp.submit} isPending={followUp.isPending} isSuccess={followUp.isSuccess} error={followUp.error} />
  );
  const canEmailSummary = followUp.available && messages.some((message) => !message.isUser && message.message.trim());

  // A plain way back to voice/chat entry from the alternate-channel stages.
  const backToGenieLink =
    channel !== "voice" ? (
      <button
        type="button"
        onClick={() => setChannel("voice")}
        className="mb-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#64748B] transition-colors hover:text-[#222B45]"
      >
        <ArrowLeft size={14} strokeWidth={2.25} />
        Back to the Genie
      </button>
    ) : null;

  const minimalVoiceStart = !demoMode && !voiceAgent.connected &&
    channel === "voice" && (!isVoiceBridgeEnabled || voiceBridge.bridgeStatus === "idle");

  // What the Genie just shared — the transcript shows it inline, so this only
  // covers the voice stage while the chat is out of view. The desktop layout
  // puts it in the closed transcript's column; everywhere else it sits under
  // the avatar.
  const sharedMedia =
    voiceAgent.connected &&
    !showTranscript &&
    !conversationCanvasActive &&
    !(isMobile && mobileDrawerOpen) &&
    (latestShare.attachments.length > 0 || mediaPending) ? (
      <GenieSharedMedia
        attachments={latestShare.attachments}
        brandColor={brand}
        agentName={agent?.name}
        agentId={agent?.id}
        onDismiss={latestShare.dismiss}
        pending={mediaPending}
      />
    ) : null;

  // The shared voice stage (avatar + start/end CTA + mic/voice/pause row +
  // human-handoff affordances) rendered identically by the template, mobile,
  // and desktop layouts. Only the presentational "voice" bits differ, so those
  // are passed in as small variants. A plain function (not a hook) so it's safe
  // to define after the early returns above.
  const voiceStageNode =
    ({
      isMobile,
      avatarSize,
      showActionRow = true,
      actionRowIsMobile,
      controlsCompact = false,
      sharedMediaUnderAvatar = true,
    }: {
      isMobile: boolean;
      avatarSize?: number;
      showActionRow?: boolean;
      actionRowIsMobile?: boolean;
      controlsCompact?: boolean;
      sharedMediaUnderAvatar?: boolean;
    }) => (
      <>
        {/* Swaps to HumanCallAvatar during a live human call so it's unmistakably
            not the genie talking. */}
        {isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" ? (
          <HumanCallAvatar
            className={STAGE_ANCHOR_CLASS}
            brandColor={brand}
            status={voiceBridge.bridgeStatus}
            embedded={isEmbedMode}
            {...(avatarSize !== undefined ? { size: avatarSize } : {})}
          />
        ) : (
          <VoiceAgentAvatar
            className={STAGE_ANCHOR_CLASS}
            integratedStart={!isEmbedMode && !demoMode}
            brandColor={brand}
            customBranding={agent?.branding}
            coverImageUrl={embedAvatarImageUrl}
            showLogoOverlay={embedAvatarShowLogoOverlay}
            sizeScale={embedAvatarSizeScale}
            onClick={minimalVoiceStart ? undefined : handleAvatarClick}
            speaking={voiceAgent.speaking}
            listening={voiceAgent.listening}
            thinking={voiceAgent.thinking}
            processing={voiceAgent.processing}
            searching={voiceAgent.searching}
            retrieving={voiceAgent.retrieving}
            isConnecting={voiceAgent.isConnecting}
            connected={voiceAgent.connected}
            embedded={isEmbedMode}
            {...(isEmbedMode && isMobile ? { size: 200 } : {})}
            {...(avatarSize !== undefined ? { size: avatarSize } : hasVoiceTrouble && !isEmbedMode ? { size: isMobile ? 220 : 280 } : {})}
          />
        )}

        {minimalVoiceStart && (
          <div className="mt-6 flex w-full max-w-sm flex-wrap justify-center gap-3 px-4" role="group" aria-label="Choose how to start your conversation">
            <Button
              type="button"
              onClick={() => void handleStartVoiceFromWelcome()}
              disabled={voiceAgent.isConnecting || memoryConsentMutation.isPending}
              aria-busy={voiceAgent.isConnecting}
              className="min-h-[44px] min-w-[120px] gap-2 rounded-full px-6 font-semibold text-white shadow-lg transition-opacity hover:opacity-90"
              style={{ background: `linear-gradient(90deg, ${brand}, ${agent?.branding?.secondaryColor || DEFAULT_BRANDING.secondaryColor})` }}
            >
              {voiceAgent.isConnecting ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Mic className="h-4 w-4" aria-hidden="true" />}
              {voiceAgent.isConnecting ? "Connecting…" : "Talk"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleStartTextFromWelcome()}
              disabled={voiceAgent.isConnecting || memoryConsentMutation.isPending}
              className="min-h-[44px] min-w-[120px] gap-2 rounded-full px-6 font-semibold"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Chat
            </Button>
          </div>
        )}

        {/* Demo mode keeps the older single-pill control — it's a stripped-down
            preview surface, not a real conversation session. Everywhere else
            (including embed), the Talk/Chat pair above and the Mic/Stop/More
            row below (via VoiceSessionActionRow's onEndConversation) fully
            own start/stop, so the old pill no longer renders there. */}
        {!consolidatedSession && demoMode && (
        <div className={minimalVoiceStart ? "mt-6 flex justify-center" : "contents"}>
        <VoiceAgentControls
          connected={voiceAgent.connected}
          isConnecting={voiceAgent.isConnecting}
          onToggle={handleAvatarClick}
          brandColor={brand}
          secondaryColor={agent?.branding?.secondaryColor}
          gradient={isEmbedMode}
          buttonText={
            controlsCompact
              ? { start: "Start", end: "End" }
              : { start: isEmbedMode || demoMode ? "Start Conversation" : "Talk to the Genie", end: "End Conversation" }
          }
          variant={!isEmbedMode && !demoMode ? "orb" : "pill"}
          className={minimalVoiceStart ? undefined : !isEmbedMode && !demoMode ? (isMobile ? "-mt-10" : "-mt-7") : controlsCompact ? "mt-3" : isEmbedMode ? "mt-5" : "mt-6 md:mt-8"}
          compact={controlsCompact}
          shape={embedCtaShape}
          colorOverride={embedCtaStyle?.color}
          colorOverride2={embedCtaStyle?.color2}
          textColorOverride={embedCtaStyle?.textColor}
          sizeScale={embedCtaStyle?.sizeScale}
        />
        </div>
        )}
        {!controlsCompact && hasVoiceTrouble && !isEmbedMode && !demoMode && (
          <div className="mt-4 w-full max-w-md px-3">
            <ConversationAlternativePrompt kind="voice" phoneNumber={phoneNumber}
              onDismiss={() => setHasVoiceTrouble(false)}
              onSwitchToText={() => void handleNoiseSwitchToText()} disabled={voiceAgent.isConnecting}
              onCall={() => { if (voiceAgent.connected) void endConversationAfterUploads(); }} />
          </div>
        )}
        {!showTranscript && followUp.showPrompt && <div className="mt-4 w-full max-w-md px-3">{followUpPrompt}</div>}

        {showActionRow && (
          <VoiceSessionActionRow
            {...contactActions}
            hidden={minimalVoiceStart}
            brandColor={brand}
            isMobile={actionRowIsMobile ?? isMobile}
            isTranscriptFeatureEnabled={!demoMode || transcriptFeatureEnabled || isEmbedMode}
            voiceConnected={voiceAgent.connected}
            isMicMuted={isMicMuted}
            onToggleMicMute={() => void handleToggleMicMute()}
            isAgentVoiceMuted={isAgentVoiceMuted}
            onToggleAgentVoice={() => void handleToggleAgentVoice()}
            isPaused={isPaused}
            onTogglePause={() => void handleTogglePause()}
            actionButtonStyle={embedActionButtonStyle}
            onOpenChat={handleOpenChat}
            infoTitle={`About ${agent?.name || "this Genie"}`}
            infoDescription={agent?.description || null}
            hasResources={hasResources}
            onOpenResources={() => setShowResources(true)}
            infoLinks={agent?.page?.info_links as InfoLinksConfig | null}
            qrLogoUrl={agent?.branding?.logoUrl || null}
            secondaryColor={agent?.branding?.secondaryColor}
            showInfoActions={false}
            onAddImage={handleAddImage}
            agentId={agent?.id ?? undefined}
            agentUserId={agent?.user_id ?? undefined}
            onSummaryCaptured={handleContactCaptured}
          />
        )}

        {isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" && (
          <HumanCallBar
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            status={voiceBridge.bridgeStatus}
            muted={voiceBridge.muted}
            destinationLabel={voiceBridge.destinationLabel}
            onToggleMute={voiceBridge.toggleMute}
            onHangup={handleHangupHumanHandoff}
          />
        )}

        {/* Always below the action row, so the controls stay right under the
            genie however much has been shared. */}
        {sharedMediaUnderAvatar && sharedMedia && (
          <div className="mt-4 flex w-full max-w-md justify-center px-3">{sharedMedia}</div>
        )}
      </>
    );

  // Render the core agent conversation interface
  const renderAgentCore = () => (
    <>
      {/* Status message — tighter in embed mode so it doesn't crowd the small popup.
          Swapped out for the Call/Email stages, which have no voice status of their own. */}
      {channel === "voice" && (
        <p
          className={cn(
            "text-center font-medium leading-[1.4] text-[#666] px-4 py-2 bg-transparent border-none rounded-xl",
            isEmbedMode
              ? "text-[0.9rem] mb-7 max-w-[260px]"
              : isMobile
                ? "text-[0.95rem] mb-6 max-w-[280px]"
                : "text-[1.05rem] mb-8 max-w-[300px]",
          )}
        >
          {getStatusMessage()}
        </p>
      )}

      {channel === "chat" ? (
        <div className="w-full max-w-[560px]" style={{ height: 520 }}>
          {backToGenieLink}
          <GenieTextChatOverlayWeb
            {...websiteChatAppearance}
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            messages={messages}
            inlineMessage={inlineMessage}
            onChangeInlineMessage={setInlineMessage}
            onSend={() => void handleInlineSend()}
            isConnecting={voiceAgent.isConnecting}
            isSpeaking={voiceAgent.speaking}
            isTextOnlyMode={isTextOnlyMode}
            isAllAudioMuted={isAllAudioMuted}
            onToggleAllAudioMute={() => void handleToggleAllAudioMute()}
            startedFromText={startedFromText}
            onCloseOverlay={() => setChannel("voice")}
            onEndChat={() => void handleEndTextChat()}
            surface="card"
            logoUrl={agentLogo}
            inlineCaptureSlot={inlineCaptureNode}
          />
        </div>
      ) : channel === "phone" ? (
        <>
          {backToGenieLink}
          <CallStage
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            phoneNumber={phoneNumber}
            callbackAvailable={callbackAvailable}
            onRequestCallback={channelRequests.requestCallback}
            pageUrl={channelPageUrl}
          />
        </>
      ) : channel === "email" ? (
        <>
          {backToGenieLink}
          <EmailStage
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            onSubmit={channelRequests.askQuestion}
            pageUrl={channelPageUrl}
          />
        </>
      ) : (
        <>
          {voiceStageNode({ isMobile, actionRowIsMobile: isMobile })}
        </>
      )}
    </>
  );

  // Shared paused overlay — rendered by both the template and default branches.
  const pauseOverlay = (
    <AnimatePresence>
      {isPaused && (
        <motion.div
          key="pause-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="fixed inset-0 z-[2000] flex items-center justify-center px-4"
          aria-modal="true"
          role="dialog"
          aria-label="Conversation paused"
        >
          <div className="absolute inset-0 bg-white/75 backdrop-blur-xl" />
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="relative w-full max-w-sm rounded-3xl bg-white p-7 text-center"
            style={{ boxShadow: "0 25px 60px -15px rgba(15,23,42,0.25), 0 0 0 1px #E5EAF0" }}
          >
            <div
              className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full"
              style={{
                background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
                boxShadow: "0 12px 32px -10px rgba(78,156,255,0.55)",
              }}
            >
              <Pause size={28} strokeWidth={2.25} className="text-white" />
            </div>
            <h2 className="text-[20px] font-semibold text-[#222B45]">Conversation paused</h2>
            <p className="mt-2 text-[14px] leading-relaxed text-[#64748B]">
              Take your time — your Genie is waiting quietly. Resume when you're ready.
            </p>
            <button
              type="button"
              onClick={() => void handleTogglePause()}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-semibold text-white shadow-lg transition-all hover:scale-[1.02] hover:opacity-95"
              style={{
                background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                boxShadow: "0 10px 28px -10px rgba(78,156,255,0.55)",
              }}
            >
              <Play size={16} strokeWidth={2.5} />
              Resume conversation
            </button>
            <button
              type="button"
              onClick={() => void handleEndTextChat()}
              className="mt-3 inline-flex w-full items-center justify-center rounded-xl px-6 py-2.5 text-[13px] font-medium text-[#64748B] transition-colors hover:text-[#222B45]"
            >
              End conversation instead
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // If template is specified in URL, wrap with TemplateWrapper
  if (templateParam) {
    return (
      <LiveAgentProvider {...liveAgentProviderProps}>
      <div onTouchStart={handleScreenTouch} style={{ position: "relative" }}>
        {helmet}
        <TemplateWrapper template={templateParam} agent={agent}>
          {renderAgentCore()}
        </TemplateWrapper>

        {helpPageUrl && !isEmbedMode && !demoMode && <div className="absolute right-5 top-5 z-[1700]"><GenieSupportLink href={helpPageUrl} /></div>}
        {followUpSheet}

        <IPhoneTapReminder
          show={voiceAgent.tapReminder.showReminder}
          onDismiss={voiceAgent.tapReminder.dismissReminder}
        />
        <NoiseEnvironmentSheet
          open={showNoiseWarning}
          brandColor={brand}
          onDismiss={dismissNoiseWarning}
          onSwitchToText={() => void handleNoiseSwitchToText()}
        />
        <ResourcesSheet
          open={showResources}
          onClose={() => setShowResources(false)}
          brandColor={brand}
          resources={resourcesConfig}
        />
        {showVoiceTroubleTrigger && (
          <div
            className="absolute inset-x-0 z-[850] flex justify-center"
            style={{ bottom: "max(14px, env(safe-area-inset-bottom))" }}
          >
            <VoiceTroubleTrigger onClick={() => setShowVoiceTrouble(true)} />
          </div>
        )}
        <VoiceTroubleSheet
          open={showVoiceTrouble}
          onDismiss={() => setShowVoiceTrouble(false)}
          brandColor={brand}
          secondaryColor={agent?.branding?.secondaryColor}
          agentId={agent?.id ?? undefined}
          agentUserId={agent?.user_id ?? undefined}
          phoneNumber={phoneNumber}
          onSwitchToText={() => void handleNoiseSwitchToText()}
          onSummaryCaptured={handleContactCaptured}
          hideSummaryCapture
          onEmailFollowUp={followUp.available ? followUp.openSheet : undefined}
        />
        {pauseOverlay}
      </div>
      </LiveAgentProvider>
    );
  }

  // Default view (no template)
  return (
    <LiveAgentProvider {...liveAgentProviderProps}>
    <div
      onTouchStart={handleScreenTouch}
      style={{
        position: "relative", // anchor absolute children (Back / Share / voice-trouble trigger) here
        minHeight: isEmbedMode || demoMode ? "100%" : "100dvh",
        // A definite height lets the canvas scroll its messages instead of growing the page.
        height: isEmbedMode || demoMode ? "100%" : conversationCanvasActive ? "100dvh" : "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        ...(isEmbedMode
          ? { background: "transparent" }
          : widgetBackgroundToCss(agent?.branding?.embedStyle?.fullPageBackground, "#F9FAFC")),
        transition: "all 0.3s ease",
        boxSizing: "border-box",
        // Prevent "page scroll jumps" when focusing inputs on mobile, AND clip
        // edge-of-viewport hover effects (e.g. magical button sparkles) so they
        // never trigger transient scrollbars when they overflow by a few px.
        overflow: "hidden",
      }}
    >
      {helmet}
      {/* The old floating "get a summary" pill is folded into the active capture
          moments now (in-conversation card, voice-trouble sheet, wrap-up) — one
          quiet persistent entry point (the Summary action-row chip) plus active
          asks at the moments that matter, instead of a second passive nag. */}

      {/* Back button for admins - hide when embedded, in portal mode, or while
          Details supplies its own return control. z-[1700]
          keeps it above the welcome gate (z-[1600]) so it's reachable before
          the visitor ever starts talking, not just once a conversation begins. */}
      {isInternalAdmin() && !isEmbedMode && !portalMode && !conversationCanvasActive && !showDetails && (
        <div className="absolute top-5 left-5 z-[1700]">
          <MagicalIconButton
            onClick={() => navigate("/genie-hub/my-genies")}
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            ariaLabel="Back to your Genies"
            hoverHint="Back to your Genies"
            hintAlign="left"
          >
            <ArrowLeft className="w-4 h-4" />
          </MagicalIconButton>
        </div>
      )}

      {/* Share button - top-right, subtle; hide when embedded, in portal mode,
          or while the full-page conversation canvas owns the chrome (it would
          float over the canvas header's controls). */}
      {!isEmbedMode && !portalMode && !conversationCanvasActive && !showWrapUp && !showDetails && (
        <div className="absolute top-5 flex items-center gap-2 z-[1700]" style={{ right: "max(20px, env(safe-area-inset-right))" }}>
          <GenieCallButton
            phoneNumber={phoneNumber}
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
          />
          {consolidatedSession && !chatPanelOpen && (
            <button type="button" onClick={handleOpenChat} aria-label="Show chat" title="Show chat"
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-full px-3 text-sm text-gray-700 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary">
              <MessageSquare size={18} aria-hidden="true" />
              <span className="hidden sm:inline">Show chat</span>
            </button>
          )}
          {/* With the chat on screen the floating Details tab would sit on top
              of the conversation, so its entry moves up here. Mobile has no
              floating tab at all, so it always gets this one, as an icon. */}
          {showDetailsAffordance && (isMobile || chatPanelOpen) && (
            <GenieDetailsLink onClick={() => setShowDetails(true)} />
          )}
          {helpPageUrl && <GenieSupportLink href={helpPageUrl} compact={canDialFromDevice(phoneNumber)} />}
          <MagicalIconButton
            onClick={handleShare}
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            ariaLabel="Share live agent"
            hoverHint="Share this Genie with others"
            hintAlign="right"
          >
            <Share2 size={18} strokeWidth={2} />
          </MagicalIconButton>
        </div>
      )}

      {followUpSheet}

      {/* Genie content - flex container for responsive layout */}
      {/* Keep desktop scroll inside chat/transcript panels, not on page wrapper */}
      {/* Layout follows the iframe/viewport width only — the embedded widget
          popup is sized (1240×760 via widget-v2.js) to host the desktop
          two-column layout, so embed mode and the standalone live-agent page
          render the same UI: avatar column on the left, transcript panel on
          the right, both visible at the same time. Narrow viewports (real
          phones, or a customer who manually shrinks the iframe) still fall
          back to MobileLiveAgentLayout. */}
      <div className={cn("min-h-0 flex-1 flex w-full box-border overflow-hidden", !isEmbedMode && !demoMode && "pt-20 md:pt-0")}>
        {isMobile ? (
          <MobileLiveAgentLayout
            chatAppearance={websiteChatAppearance}
            suppressTranscript={showWrapUp && !voiceAgent.connected && !voiceAgent.isConnecting}
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            messages={chatMessages}
            forceTranscriptOpen={showTranscript}
            resourcesRail={
              // Before a conversation, the support page holds the guides.
              // This rail stays available
              // once a conversation is actually live, contextual surfacing
              // (surfacedId) included.
              voiceAgent.connected && railItems.length > 0 && !demoMode ? (
                <ResourcesRail
                  resources={resourcesConfig}
                  brandColor={brand}
                  variant="horizontal"
                  compact={voiceAgent.connected}
                  surfacedId={surfacedResourceId}
                  onOpenAll={() => setShowResources(true)}
                  onSelect={handleRailSelect}
                  onImpression={handleRailImpression}
                />
              ) : null
            }
            rightInlineIcons={
              <VoiceSessionActionRow
                {...contactActions}
                hidden={minimalVoiceStart}
                brandColor={brand}
                isMobile={true}
                isTranscriptFeatureEnabled={!demoMode || transcriptFeatureEnabled || isEmbedMode}
                voiceConnected={voiceAgent.connected}
                isMicMuted={isMicMuted}
                onToggleMicMute={() => void handleToggleMicMute()}
                isAgentVoiceMuted={isAgentVoiceMuted}
                onToggleAgentVoice={() => void handleToggleAgentVoice()}
                isPaused={isPaused}
                onTogglePause={() => void handleTogglePause()}
                onOpenChat={handleOpenChat}
                infoTitle={`About ${agent?.name || "this Genie"}`}
                infoDescription={agent?.description || null}
                infoLinks={agent?.page?.info_links as InfoLinksConfig | null}
                qrLogoUrl={agent?.branding?.logoUrl || null}
                secondaryColor={agent?.branding?.secondaryColor}
                showInfoActions={false}
                onAddImage={handleAddImage}
                agentId={agent?.id ?? undefined}
                agentUserId={agent?.user_id ?? undefined}
                onSummaryCaptured={handleContactCaptured}
                actionButtonStyle={embedActionButtonStyle}
              />
            }
            drawerHeaderVoiceControls={
              /* Compact mode for the slim drawer header:
                 - neutralise VoiceSessionActionRow's mt-4 lg:mt-8 + px-3
                 - hide the per-button text labels (hover tooltips still work)
                   so the row matches the icon-only cog + close on the right
                 - keep tight horizontal spacing
                 - align bottom so the 36px buttons sit on the same baseline as
                   the 32px cog/close on the right side of the header */
              <div className="-my-1 flex flex-col items-start gap-2 [&>div]:!mt-0 [&>div]:!px-0 [&>div>div]:!gap-2 [&_span[class*='max-w-\\[5\\.5rem\\]']]:!hidden">
                {/* Email summary lives in the More menu here — as a header
                    pill it filled most of a phone screen. */}
                <VoiceSessionActionRow
                  {...contactActions}
                  onEmailSummary={canEmailSummary ? followUp.openSummarySheet : undefined}
                  emailSummaryLabel={followUp.purpose === "summary" && followUp.isSuccess ? "Summary requested" : "Email summary"}
                  hidden={minimalVoiceStart}
                  brandColor={brand}
                  isMobile={true}
                  isTranscriptFeatureEnabled={!demoMode || transcriptFeatureEnabled || isEmbedMode}
                  voiceConnected={voiceAgent.connected}
                  isMicMuted={isMicMuted}
                  onToggleMicMute={() => void handleToggleMicMute()}
                  isAgentVoiceMuted={isAgentVoiceMuted}
                  onToggleAgentVoice={() => void handleToggleAgentVoice()}
                  isPaused={isPaused}
                  onTogglePause={() => void handleTogglePause()}
                  onOpenChat={handleOpenChat}
                  infoTitle={`About ${agent?.name || "this Genie"}`}
                  infoDescription={agent?.description || null}
                  infoLinks={agent?.page?.info_links as InfoLinksConfig | null}
                  qrLogoUrl={agent?.branding?.logoUrl || null}
                  showSummaryCapture={false}
                  actionButtonStyle={embedActionButtonStyle}
                />
              </div>
            }
            isConnected={voiceAgent.connected}
            isListening={voiceAgent.listening}
            isConnecting={voiceAgent.isConnecting}
            isSpeaking={voiceAgent.speaking}
            isAllAudioMuted={isAllAudioMuted}
            onToggleAllAudioMute={() => void handleToggleAllAudioMute()}
            onEndConversation={consolidatedSession ? undefined : () => void endConversationAfterUploads()}
            isTextOnlyMode={isTextOnlyMode}
            inlineMessage={inlineMessage}
            onChangeInlineMessage={setInlineMessage}
            onSendChat={() => void handleInlineSend()}
            startedFromText={startedFromText}
            onEndChat={() => void handleEndTextChat()}
            attachments={attachments.map((att) => ({
              id: att.id,
              previewUrl: att.previewUrl,
              status: att.status,
              analysisText: att.analysisText,
            }))}
            onRemoveAttachment={removeAttachment}
            onOpenFilePicker={() => {
              const input = document.getElementById("genie-chat-attachments") as HTMLInputElement | null;
              if (input) {
                input.click();
              }
            }}
            onUpdateAttachmentAnalysis={updateAttachmentAnalysis}
            imageUploadPendingCount={pendingImageUploads}
            imageUploadFailedCount={failedImageUploads}
            imageUploadSaving={isSavingImages}
            onRetryImageUploads={retryFailedImageUploads}
            onDrawerOpenChange={(open) => {
              setMobileDrawerOpen(open);
              setShowTranscript(open);
            }}
            inlineCaptureSlot={inlineCaptureNode}
            belowActionRow={sharedMedia ? <div className="flex w-full justify-center">{sharedMedia}</div> : undefined}
          >
            {/* Status message — tighter in embed mode so the small popup fits the rest.
                Swapped out for the Call/Email stages, which have no voice status. */}
            {channel === "voice" && (
              <p
                className={cn(
                  "text-center font-medium leading-[1.4] text-[#666] px-4 py-2 bg-transparent border-none rounded-xl",
                  isEmbedMode
                    ? "text-[0.9rem] mb-7 max-w-[260px]"
                    : isMobile
                      ? "text-[0.95rem] mb-6 max-w-[280px]"
                      : "text-[1.05rem] mb-8 max-w-[300px]",
                )}
              >
                {getStatusMessage()}
              </p>
            )}

            {channel === "phone" ? (
              <>
                {backToGenieLink}
                <CallStage
                  brandColor={brand}
                  secondaryColor={agent?.branding?.secondaryColor}
                  agentName={agent?.name}
                  phoneNumber={phoneNumber}
                  callbackAvailable={callbackAvailable}
                  onRequestCallback={channelRequests.requestCallback}
                  pageUrl={channelPageUrl}
                />
              </>
            ) : channel === "email" ? (
              <>
                {backToGenieLink}
                <EmailStage
                  brandColor={brand}
                  secondaryColor={agent?.branding?.secondaryColor}
                  agentName={agent?.name}
                  onSubmit={channelRequests.askQuestion}
                  pageUrl={channelPageUrl}
                />
              </>
            ) : (
              <>
                {voiceStageNode({
                  isMobile: true,
                  showActionRow: false,
                  // The layout renders the action row itself; the share goes in its slot beneath it.
                  sharedMediaUnderAvatar: false,
                })}
                {/* Icons moved into MobileLiveAgentLayout inline row via rightInlineIcons */}
              </>
            )}
          </MobileLiveAgentLayout>
        ) : conversationCanvasActive ? (
          <GenieConversationCanvas
            {...websiteChatAppearance}
            onRestoreSplitView={fullTextView ? () => setFullTextView(false) : undefined}
            audioControls={fullTextView && voiceAgent.connected && !startedFromText ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 w-11 rounded-full p-0"
                  onClick={() => void handleToggleMicMute()}
                  aria-label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
                  title={isMicMuted ? "Unmute microphone" : "Mute microphone"}
                  aria-pressed={isMicMuted}
                >
                  {isMicMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 w-11 rounded-full p-0"
                  onClick={() => void handleToggleAgentVoice()}
                  aria-label={isAgentVoiceMuted ? "Unmute Genie voice" : "Mute Genie voice"}
                  title={isAgentVoiceMuted ? "Unmute Genie voice" : "Mute Genie voice"}
                  aria-pressed={isAgentVoiceMuted}
                >
                  {isAgentVoiceMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
              </>
            ) : undefined}
            helpPageUrl={helpPageUrl}
            onOpenDetails={showDetailsAffordance ? () => setShowDetails(true) : undefined}
            onEmailSummary={canEmailSummary ? followUp.openSummarySheet : undefined}
            emailSummaryPending={followUp.isPending}
            emailSummaryRequested={followUp.purpose === "summary" && followUp.isSuccess}
            onEmailFollowUp={followUp.available ? followUp.openSheet : undefined}
            onCallGenie={phoneNumber ? () => setChannel("phone") : undefined}
            onTalkToHuman={onTalkToHuman}
            brandColor={brand}
            secondaryColor={agent?.branding?.secondaryColor}
            agentName={agent?.name}
            logoUrl={agentLogo}
            messages={chatMessages}
            inlineMessage={inlineMessage}
            onChangeInlineMessage={setInlineMessage}
            onSend={() => void handleInlineSend()}
            isConnecting={voiceAgent.isConnecting}
            isSpeaking={voiceAgent.speaking}
            startedFromText={startedFromText}
            voiceConnected={voiceAgent.connected}
            onEndConversation={() => void endConversationAfterUploads()}
            onEndChat={() => void handleEndTextChat()}
            onCloseOverlay={() => {
              setFullTextView(false);
              setShowTranscript(false);
              setChatExpanded(false);
              if (channel === "chat") setChannel("voice");
            }}
            attachments={attachments.map((att) => ({
              id: att.id,
              previewUrl: att.previewUrl,
              status: att.status,
              analysisText: att.analysisText,
            }))}
            onRemoveAttachment={removeAttachment}
            onOpenFilePicker={() => {
              const input = document.getElementById("genie-chat-attachments") as HTMLInputElement | null;
              if (input) {
                input.click();
              }
            }}
            onUpdateAttachmentAnalysis={updateAttachmentAnalysis}
            imageUploadPendingCount={pendingImageUploads}
            imageUploadFailedCount={failedImageUploads}
            imageUploadSaving={isSavingImages}
            onRetryImageUploads={retryFailedImageUploads}
            inlineCaptureSlot={followUp.showPrompt ? undefined : inlineCaptureNode}
          />
        ) : (
          <motion.div
            layout={!startedFromText}
            className={cn(
              "relative flex min-h-0 w-full mx-auto items-center justify-center gap-6 px-6",
              // Framed, the viewport units can exceed the frame, so the stage
              // takes its height from the frame itself.
              isEmbedMode ? "h-full" : "h-[calc(100dvh-60px)]",
              // The rail adds a third column, so the frame needs room for it
              // on top of the genie + transcript columns.
              railInGutter
                ? chatExpanded
                  ? "max-w-[1820px]"
                  : "max-w-[1600px]"
                : chatExpanded
                  ? "max-w-[1500px]"
                  : "max-w-[1280px]",
            )}
          >
            {/* Full-screen transcript layout is only for conversations started from text. */}
            {(() => {
              const useTextOnlyDesktopLayout = startedFromText;
              // Chat-first focus mode: shrink the genie column, grow the chat column.
              // Genie + branding stay visible — just compact. Column needs to fit the
              // End button (minWidth 160) + breathing room around the 160px avatar.
              const chatVisible = showTranscript && (transcriptFeatureEnabled || isTextOnlyMode || isEmbedMode);
              const rightColumnVisible = chatVisible;
              // Narrow screens and frames have no room for a side column, so
              // there the share stays under the avatar.
              const sharedMediaInColumn = stageFit.fullGenieBesideSideColumn;
              const sharedMediaColumnVisible = sharedMediaInColumn && !rightColumnVisible && !!sharedMedia;
              const chatBeside = rightColumnVisible && !useTextOnlyDesktopLayout;
              // The rail keeps its gutter only while every column fits the
              // viewport; otherwise it lays down as a band under the action
              // row, or its resources show as cards in the chat once that is open.
              const railGutterVisible =
                railInGutter &&
                !(rightColumnVisible && useTextOnlyDesktopLayout) &&
                (chatBeside || sharedMediaColumnVisible ? stageFit.railBesideSideColumn : stageFit.railGutters);
              // Too narrow for the full genie column beside the transcript:
              // fall back to the chat-focus sizes so nothing leaves the viewport.
              const compactGenie = chatExpanded || (chatBeside && !stageFit.fullGenieBesideSideColumn);
              const genieColumnWidth = compactGenie ? 240 : 620;
              const chatColumnClass = chatExpanded
                ? "flex-[0_1_980px] min-w-[360px]"
                : "flex-[0_1_560px] min-w-[360px]";
              const avatarSize = compactGenie ? 160 : undefined;
              return (
                <AnimatePresence initial={false}>
                  {railGutterVisible && (
                    <div key="desktop-resources-rail" className={RAIL_GUTTER_CLASS}>
                      {/* Pre-conversation, the support-page link covers guides
                          and resources — the gutter itself stays reserved (so the
                          layout never jumps) but the rail only mounts once a
                          conversation is actually live. */}
                      {voiceAgent.connected && (
                        <ResourcesRail
                          resources={resourcesConfig}
                          brandColor={brand}
                          variant="vertical"
                          compact={voiceAgent.connected}
                          surfacedId={surfacedResourceId}
                          onOpenAll={() => setShowResources(true)}
                          onSelect={handleRailSelect}
                          onImpression={handleRailImpression}
                        />
                      )}
                    </div>
                  )}

                  {!(showTranscript && useTextOnlyDesktopLayout) && (
                    <motion.div
                      key="desktop-voice-core"
                      layout
                      initial={{ opacity: 0, scaleX: 0.9 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      exit={{ opacity: 0, scaleX: 0.9 }}
                      transition={{
                        duration: 0.22,
                        ease: "easeOut",
                        layout: { duration: 0.24, ease: "easeInOut" },
                      }}
                    >
                      <LiveAgentContainer
                        showTranscript={false}
                        isMobile={false}
                        brandColor={brand}
                        agentName={agent?.name}
                        messages={messages}
                        onCloseTranscript={() => setShowTranscript(false)}
                        leftWidthPx={genieColumnWidth}
                        transcriptWidthPx={460}
                        gapPx={16}
                      >
                        {/* Status message — hidden in chat-focus mode to keep the
                            genie column tight, and swapped out entirely for the
                            Call/Email stages, which have no voice status of their
                            own. Same sizing for embed + standalone so the widget
                            popup mirrors the live agent page exactly. */}
                        {!compactGenie && channel === "voice" && (
                          <p
                            className={cn(
                              "text-center font-medium leading-[1.4] text-[#666] px-4 py-2 bg-transparent border-none rounded-xl",
                              isMobile ? "text-[0.95rem] mb-6 max-w-[280px]" : "text-[1.05rem] mb-8 max-w-[300px]",
                            )}
                          >
                            {getStatusMessage()}
                          </p>
                        )}

                        {channel === "chat" ? (
                          // Ready-to-type card, handing off to the full conversation
                          // canvas once the visitor sends their first message.
                          <div className="w-full max-w-[560px]" style={{ height: 520 }}>
                            {backToGenieLink}
                            <GenieTextChatOverlayWeb
                              {...websiteChatAppearance}
                              brandColor={brand}
                              secondaryColor={agent?.branding?.secondaryColor}
                              agentName={agent?.name}
                              messages={messages}
                              inlineMessage={inlineMessage}
                              onChangeInlineMessage={setInlineMessage}
                              onSend={() => void handleInlineSend()}
                              isConnecting={voiceAgent.isConnecting}
                              isSpeaking={voiceAgent.speaking}
                              isTextOnlyMode={isTextOnlyMode}
                              isAllAudioMuted={isAllAudioMuted}
                              onToggleAllAudioMute={() => void handleToggleAllAudioMute()}
                              startedFromText={startedFromText}
                              onCloseOverlay={() => setChannel("voice")}
                              onEndChat={() => void handleEndTextChat()}
                              surface="card"
                              logoUrl={agentLogo}
                              inlineCaptureSlot={inlineCaptureNode}
                            />
                          </div>
                        ) : channel === "phone" ? (
                          <>
                            {backToGenieLink}
                            <CallStage
                              brandColor={brand}
                              secondaryColor={agent?.branding?.secondaryColor}
                              agentName={agent?.name}
                              phoneNumber={phoneNumber}
                              callbackAvailable={callbackAvailable}
                              onRequestCallback={channelRequests.requestCallback}
                              pageUrl={channelPageUrl}
                            />
                          </>
                        ) : channel === "email" ? (
                          <>
                            {backToGenieLink}
                            <EmailStage
                              brandColor={brand}
                              secondaryColor={agent?.branding?.secondaryColor}
                              agentName={agent?.name}
                              onSubmit={channelRequests.askQuestion}
                              pageUrl={channelPageUrl}
                            />
                          </>
                        ) : (
                          <>
                            {voiceStageNode({
                              isMobile: false,
                              avatarSize,
                              actionRowIsMobile: false,
                              controlsCompact: compactGenie,
                              sharedMediaUnderAvatar: !sharedMediaInColumn,
                            })}

                            {/* Without room for a third column the rail lays
                                down as a band under the action row rather than
                                taking a gutter. Same component, same items. */}
                            {railInGutter &&
                              !railGutterVisible &&
                              !rightColumnVisible &&
                              voiceAgent.connected &&
                              railItems.length > 0 &&
                              !compactGenie && (
                              <div className="mt-5 w-full text-left">
                                <ResourcesRail
                                  resources={resourcesConfig}
                                  brandColor={brand}
                                  variant="horizontal"
                                  compact={voiceAgent.connected}
                                  surfacedId={surfacedResourceId}
                                  onOpenAll={() => setShowResources(true)}
                                  onSelect={handleRailSelect}
                                  onImpression={handleRailImpression}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </LiveAgentContainer>
                    </motion.div>
                  )}

                  {rightColumnVisible && (
                    <motion.div
                      key="desktop-chat-overlay"
                      layout={!useTextOnlyDesktopLayout}
                      initial={useTextOnlyDesktopLayout ? { opacity: 0, y: 10 } : { opacity: 0, scaleX: 0.9 }}
                      animate={useTextOnlyDesktopLayout ? { opacity: 1, y: 0 } : { opacity: 1, scaleX: 1 }}
                      exit={useTextOnlyDesktopLayout ? { opacity: 0, y: 10 } : { opacity: 0, scaleX: 0.9 }}
                      transition={{
                        duration: useTextOnlyDesktopLayout ? 0.3 : 0.22,
                        ease: "easeOut",
                        layout: { duration: 0.24, ease: "easeInOut" },
                      }}
                      style={useTextOnlyDesktopLayout ? undefined : { originX: 0 }}
                      className={cn(
                        "relative",
                        useTextOnlyDesktopLayout
                          ? cn(
                              "flex min-h-0 h-full w-full max-w-[860px] items-center justify-center",
                              isEmbedMode ? "max-h-[calc(100%-30px)]" : "max-h-[calc(100dvh-90px)]",
                            )
                          : chatColumnClass,
                      )}
                    >
                      {chatVisible && (
                        <GenieTextChatOverlayWeb
                          {...websiteChatAppearance}
                          brandColor={brand}
                          secondaryColor={agent?.branding?.secondaryColor}
                          agentName={agent?.name}
                          // The gutter rail already shows the resources.
                          messages={railGutterVisible ? messages : chatMessages}
                          inlineMessage={inlineMessage}
                          onChangeInlineMessage={setInlineMessage}
                          onSend={() => void handleInlineSend()}
                          isConnecting={voiceAgent.isConnecting}
                          isSpeaking={voiceAgent.speaking}
                          isTextOnlyMode={isTextOnlyMode}
                          isAllAudioMuted={isAllAudioMuted}
                          onToggleAllAudioMute={() => void handleToggleAllAudioMute()}
                          startedFromText={startedFromText}
                          onCloseOverlay={() => {
                            setShowTranscript(false);
                            setChatExpanded(false);
                          }}
                          onEndChat={() => void handleEndTextChat()}
                          chatExpanded={!useTextOnlyDesktopLayout && chatExpanded}
                          onToggleExpand={useTextOnlyDesktopLayout ? undefined : () => setChatExpanded((v) => !v)}
                          onOpenFullTextView={!isEmbedMode && !demoMode && !useTextOnlyDesktopLayout
                            ? () => setFullTextView(true)
                            : undefined}
                          attachments={attachments.map((att) => ({
                            id: att.id,
                            previewUrl: att.previewUrl,
                            status: att.status,
                            analysisText: att.analysisText,
                          }))}
                          onRemoveAttachment={removeAttachment}
                          onOpenFilePicker={() => {
                            const input = document.getElementById("genie-chat-attachments") as HTMLInputElement | null;
                            if (input) {
                              input.click();
                            }
                          }}
                          onUpdateAttachmentAnalysis={updateAttachmentAnalysis}
                          voiceConnected={voiceAgent.connected}
                          onEndConversation={consolidatedSession ? undefined : () => void endConversationAfterUploads()}
                          imageUploadPendingCount={pendingImageUploads}
                          imageUploadFailedCount={failedImageUploads}
                          imageUploadSaving={isSavingImages}
                          onRetryImageUploads={retryFailedImageUploads}
                          surface={useTextOnlyDesktopLayout ? "card" : "bare"}
                          logoUrl={agentLogo}
                          inlineCaptureSlot={inlineCaptureNode}
                        />
                      )}
                    </motion.div>
                  )}

                  {/* With the transcript closed, what the Genie shares takes its
                      place beside the avatar instead of stacking under it. */}
                  {sharedMediaColumnVisible && (
                    <motion.div
                      key="desktop-shared-media"
                      layout
                      initial={{ opacity: 0, scaleX: 0.9 }}
                      animate={{ opacity: 1, scaleX: 1 }}
                      exit={{ opacity: 0, scaleX: 0.9 }}
                      transition={{
                        duration: 0.22,
                        ease: "easeOut",
                        layout: { duration: 0.24, ease: "easeInOut" },
                      }}
                      style={{ originX: 0 }}
                      className={SHARED_MEDIA_COLUMN_CLASS}
                    >
                      {sharedMedia}
                    </motion.div>
                  )}

                  {/* Keeps the genie column optically centred when the rail is
                      in the left gutter but the transcript column is closed. */}
                  {railGutterVisible && !rightColumnVisible && !sharedMediaColumnVisible && (
                    <div key="desktop-rail-spacer" aria-hidden className={RAIL_GUTTER_CLASS} />
                  )}
                </AnimatePresence>
              );
            })()}
          </motion.div>
        )}
      </div>

      {/* Bottom text chat input — only when voice session active and mic muted */}
      <AnimatePresence>
        {/* An attachment on its own is enough to bring the composer up. Without
            that, "Add photo" from the action row would analyse the image and
            then show the visitor nothing to send it with. */}
        {((voiceAgent.connected && isMicMuted) || attachments.length > 0) &&
          !isPaused &&
          !showTranscript &&
          !(isMobile && mobileDrawerOpen) && (
          <MobileAwareBottomInput
            isMobile={isMobile}
            inlineMessage={inlineMessage}
            setInlineMessage={setInlineMessage}
            onSend={() => void handleInlineSend()}
            attachments={attachments}
            removeAttachment={removeAttachment}
            updateAttachmentAnalysis={updateAttachmentAnalysis}
          />
        )}
      </AnimatePresence>

      {/* "Made magical by Help Genie" footer — only in embed mode.
          Extra top margin gives the action row above some breathing room
          so the start screen doesn't feel cramped against the footer. */}
      {isEmbedMode && !agent?.branding?.hideAttribution && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 16px",
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <HelpGenieLogo size={18} />
          <a
            href={`${window.location.origin}/live-agent/${url_name}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: brand,
              textDecoration: "none",
              transition: "opacity 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = "0.7";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            Made magical by Help Genie
          </a>
        </div>
      )}


      {/* iPhone tap reminder */}
      <IPhoneTapReminder
        show={voiceAgent.tapReminder.showReminder}
        onDismiss={voiceAgent.tapReminder.dismissReminder}
      />

      <NoiseEnvironmentSheet
        open={showNoiseWarning}
        brandColor={brand}
        onDismiss={dismissNoiseWarning}
        onSwitchToText={() => void handleNoiseSwitchToText()}
      />

      <ResourcesSheet
        open={showResources}
        onClose={() => setShowResources(false)}
        brandColor={brand}
        resources={resourcesConfig}
      />

      {/* Voice-trouble fallback — subtle manual trigger while live, plus an
          automatic offer when cheap frontend signals suggest voice is
          struggling (see useVoiceTroubleSignals). A rescued wobble is a lead. */}
      {showVoiceTroubleTrigger && (
        <div
          className="absolute inset-x-0 z-[850] flex justify-center"
          style={{ bottom: "max(14px, env(safe-area-inset-bottom))" }}
        >
          <VoiceTroubleTrigger onClick={() => setShowVoiceTrouble(true)} />
        </div>
      )}
      <VoiceTroubleSheet
        open={showVoiceTrouble}
        hideSummaryCapture
        onEmailFollowUp={followUp.available ? followUp.openSheet : undefined}
        onDismiss={() => setShowVoiceTrouble(false)}
        brandColor={brand}
        secondaryColor={agent?.branding?.secondaryColor}
        agentId={agent?.id ?? undefined}
        agentUserId={agent?.user_id ?? undefined}
        phoneNumber={phoneNumber}
        onSwitchToText={() => void handleNoiseSwitchToText()}
        onSummaryCaptured={handleContactCaptured}
      />

      {/* Frosted-glass veil — the start screen softens into haze as the Details
          view is summoned, so the two states blur into each other rather than
          hard-cutting. */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            key="details-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="absolute inset-0 z-[1400]"
            style={{
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              background: "rgba(249,250,252,0.35)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Right-edge Details control → summons the Details view. Styled to match
          the bottom action row (circular button + label). It dissolves away as
          the panel forms and reforms as the panel clears, so the loop is seamless.
          Desktop only — on mobile this floats over the avatar, so the Details
          entry is folded into the bottom action row instead. Voice stage only:
          with the chat on screen it would float over the conversation, so the
          entry moves to the header row (GenieDetailsLink) or, in the full-page
          canvas, to its More menu. */}
      <AnimatePresence>
        {showDetailsAffordance && !showDetails && !isMobile && !chatPanelOpen && !conversationCanvasActive && (
          <motion.div
            key="details-tab"
            initial={{ opacity: 0, x: 16, y: "-50%" }}
            animate={{ opacity: 1, x: 0, y: "-50%" }}
            exit={{ opacity: 0, x: 16, y: "-50%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="absolute right-5 top-1/2 z-[1000] sm:right-7"
          >
            <GenieDetailsTab
              onClick={() => setShowDetails(true)}
              brandColor={brand}
              secondaryColor={agent?.branding?.secondaryColor}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <GenieDetailsPanel
        open={showDetails}
        onClose={() => setShowDetails(false)}
        agentDescription={agent?.description || null}
        resources={resourcesConfig}
        helpPageUrl={helpPageUrl}
        helpHub={helpHubData}
        surfacedResourceId={surfacedResourceId}
        shareUrl={shareUrl}
        memoryConsent={canManageCustomerMemory ? agent.memoryConsent || "unset" : undefined}
        memoryUpdating={memoryConsentMutation.isPending}
        onMemoryConsentChange={canManageCustomerMemory ? (enabled) => void updateCustomerMemoryConsent(enabled) : undefined}
      />

      {/* Configured redirects retain priority. Finish active saves and any
          visitor-opened email follow-up before leaving for the support page. */}
      {showWrapUp && helpPageUrl && !agent.redirect_url &&
      !voiceAgent.connected && !voiceAgent.isConnecting &&
      !isSavingImages && pendingImageUploads === 0 && failedImageUploads === 0 &&
      !followUp.open && !followUp.isPending ? (
        <Navigate to={helpPageUrl} replace />
      ) : (
        <ConversationWrapUp
          open={showWrapUp && !voiceAgent.connected && !voiceAgent.isConnecting}
          backgroundStyle={
            isEmbedMode ? undefined : widgetBackgroundToCss(agent?.branding?.embedStyle?.fullPageBackground, "#F9FAFC")
          }
          textColor={websiteChatAppearance?.welcomeTextColor}
          darkSurface={!isEmbedMode && isDarkBackground(agent?.branding?.embedStyle?.fullPageBackground)}
          followUpSlot={followUp.isSuccess ? <p role="status" className="text-sm text-gray-600">Your email follow-up request has been sent.</p> : followUpPrompt}
          onClose={() => {
            setShowWrapUp(false);
            handleStartOver();
          }}
          resources={resourcesConfig}
          conversationId={lastConversationId}
          transcript={messages}
          phoneNumber={phoneNumber}
          helpPageUrl={helpPageUrl}
          emailAddress={emailAddress}
          guideCount={guideCount}
          onSwitchToText={() => {
            setShowWrapUp(false);
            void handleStartTextFromWelcome();
          }}
        />
      )}

      {isVoiceBridgeEnabled && (
        <HumanHandoffPrompt
          open={showHandoffPrompt}
          onOpenChange={(open) => {
            if (open) openHandoffPrompt();
            else closeHandoffPrompt();
          }}
          brandColor={brand}
          agentName={agent?.name}
          isConnecting={voiceBridge.bridgeStatus === "requesting-token" || voiceBridge.bridgeStatus === "connecting"}
          error={handoffError}
          onConfirm={() => void handleConfirmHumanHandoff()}
          agentId={agent?.id ?? undefined}
          agentUserId={agent?.user_id ?? undefined}
          onSummaryCaptured={handleContactCaptured}
        />
      )}

      {/* "What to expect" welcome gate — sits over the calm start screen the first
          time a visitor opens this Genie so the voice never ambushes them. */}
      <GenieWelcomeScreen
        open={
          showWelcome &&
          channel === "voice" &&
          (!welcomeSeenForGenie || (canManageCustomerMemory && agent.memoryConsent === "unset")) &&
          !isEmbedMode &&
          !demoMode &&
          !showInviteGate &&
          !voiceAgent.connected &&
          !voiceAgent.isConnecting
        }
        brandColor={brand}
        secondaryColor={agent?.branding?.secondaryColor}
        agentName={agent?.name}
        agentDescription={agent?.description || null}
        logoUrl={agentLogo || null}
        hideAttribution={agent?.branding?.hideAttribution}
        hasPhone={!!phoneNumber}
        hasEmail={!!emailAddress}
        phoneNumber={phoneNumber}
        emailAddress={emailAddress}
        pageUrl={shareUrl}
        urlName={agent?.url_name ?? url_name}
        agentId={agent?.id ?? undefined}
        agentUserId={agent?.user_id ?? undefined}
        onSummaryCaptured={handleContactCaptured}
        memoryConsent={canManageCustomerMemory ? agent.memoryConsent || "unset" : undefined}
        memoryUpdating={memoryConsentMutation.isPending}
        onMemoryConsentChange={canManageCustomerMemory ? (enabled) => void updateCustomerMemoryConsent(enabled) : undefined}
        onStartVoice={handleStartVoiceFromWelcome}
        onStartText={handleStartTextFromWelcome}
      />

      {pauseOverlay}

      {/* Hidden file input for image attachments */}
      <input
        id="genie-chat-attachments"
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          // Reset input value so selecting the same file again still triggers onChange.
          event.target.value = "";
        }}
      />

      {/* Second input, camera-first. A separate element rather than setting
          `capture` on the one above and clicking in the same tick: that races
          React's reconciliation, and Safari caches the picker configuration on
          first open. `capture` is ignored on desktop, and `multiple` is
          meaningless alongside it, so it is left off. */}
      <input
        id="genie-chat-camera"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          addFiles(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
    </LiveAgentProvider>
  );
}

function LiveAgentPage(props: AgentPageProps = {}) {
  const isEmbeddedWidget =
    typeof window !== "undefined" &&
    !props.portalMode &&
    !props.demoMode &&
    new URLSearchParams(window.location.search).get("embed") === "true" &&
    window.parent !== window;

  if (isEmbeddedWidget) return <EmbedLiveAgentExperience agentPageData={props.agentPageData} />;
  return <FullPageLiveAgentExperience {...props} />;
}

export default LiveAgentPage;
